import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, TERRAIN_PRO_APP_BASE_URL } from '../config/api';
import './InteractiveDemo.css';

interface StepState {
  stepNumber: number;
  status: 'locked' | 'ready' | 'running' | 'success' | 'error';
  requestData: Record<string, any>;
  responseData: Record<string, any> | null;
  errorMessage: string | null;
  startTime: Date | null;
  endTime: Date | null;
  statusMessage?: string;
}

interface VendorPreset {
  min_pile_reveal: { SI: number; Imperial: number };  // m (SI) / ft (Imperial)
  reveal_window: { SI: number; Imperial: number };     // mm (SI) / in (Imperial)
  max_slope_north: number;                             // degrees
  max_slope_south: number;                             // degrees
  max_articulation_angle: number;                      // degrees (UI "Maximum slope change")
}

// Tracker-vendor parameter presets. Mirrors predefinedMenuOptions in the Terrain Pro
// Angular app (analysis-tab.component.ts). SI/Imperial are independent presets, not
// conversions. reveal_window is in display units (mm SI / in Imperial); the backend
// converts it to storage units on run. Selecting a preset prefills the editable fields.
const VENDOR_PRESETS: Record<string, VendorPreset> = {
  'Horizon':   { min_pile_reveal: { SI: 1.22, Imperial: 4 }, reveal_window: { SI: 600, Imperial: 24 }, max_slope_north: 8.5,  max_slope_south: 8.5,  max_articulation_angle: 0 },
  'XTR':       { min_pile_reveal: { SI: 1.22, Imperial: 4 }, reveal_window: { SI: 600, Imperial: 24 }, max_slope_north: 8.5,  max_slope_south: 8.5,  max_articulation_angle: 0.75 },
  'XTR PRO':   { min_pile_reveal: { SI: 1.22, Imperial: 4 }, reveal_window: { SI: 600, Imperial: 24 }, max_slope_north: 8.5,  max_slope_south: 8.5,  max_articulation_angle: 1.5 },
  'DuraTrack': { min_pile_reveal: { SI: 1.22, Imperial: 4 }, reveal_window: { SI: 600, Imperial: 24 }, max_slope_north: 8.5,  max_slope_south: 8.5,  max_articulation_angle: 0 },
  'OmniTrack': { min_pile_reveal: { SI: 1.22, Imperial: 4 }, reveal_window: { SI: 600, Imperial: 24 }, max_slope_north: 8.5,  max_slope_south: 8.5,  max_articulation_angle: 1 },
  'ATT stb':   { min_pile_reveal: { SI: 1.22, Imperial: 4 }, reveal_window: { SI: 152, Imperial: 6 },  max_slope_north: 20.5, max_slope_south: 20.5, max_articulation_angle: 2 },
  'ATT art':   { min_pile_reveal: { SI: 1.22, Imperial: 4 }, reveal_window: { SI: 0,   Imperial: 0 },  max_slope_north: 20.5, max_slope_south: 20.5, max_articulation_angle: 14.6 },
};

const InteractiveDemo: React.FC = () => {
  const { authToken, setAuthToken } = useAuth();
  
  const [localUserInfo, setLocalUserInfo] = useState<{ user_email: string; user_uuid: string } | null>(null);
  
  const [steps, setSteps] = useState<StepState[]>([
    { stepNumber: 1, status: 'ready', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
    { stepNumber: 2, status: 'locked', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
    { stepNumber: 3, status: 'locked', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
    { stepNumber: 4, status: 'locked', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
    { stepNumber: 5, status: 'locked', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
  ]);

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [projectName, setProjectName] = useState('Demo Project');
  const [layoutName, setLayoutName] = useState('Demo Layout');

  // Project selection: existing dropdown or create new
  const [projectMode, setProjectMode] = useState<'existing' | 'new'>('new');
  const [projectsList, setProjectsList] = useState<any[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [projectSearch, setProjectSearch] = useState('');
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const projectDropdownRef = useRef<HTMLDivElement>(null);
  const [projectLat, setProjectLat] = useState('');
  const [projectLon, setProjectLon] = useState('');
  const [geoInfo, setGeoInfo] = useState<{ city?: string; state?: string; country?: string; elevation?: number; timezone?: number } | null>(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const geoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [emailInput, setEmailInput] = useState('');
  const [emailStatus, setEmailStatus] = useState<{ type: 'idle' | 'loading' | 'success' | 'error'; message: string }>({ type: 'idle', message: '' });
  
  // For Step 3: DXF layer/block/nested-block selection
  const [availableLayers, setAvailableLayers] = useState<string[]>([]);
  const [availableBlocks, setAvailableBlocks] = useState<string[]>([]);
  const [availableNestedBlocks, setAvailableNestedBlocks] = useState<string[]>([]);
  const [selectedLayers, setSelectedLayers] = useState<string[]>([]);
  const [selectedBlocks, setSelectedBlocks] = useState<string[]>([]);
  const [selectedNestedBlocks, setSelectedNestedBlocks] = useState<string[]>([]);
  
  // Step 3: Elevation data source toggle + satellite search
  const [elevationSource, setElevationSource] = useState<'contour' | 'satellite'>('contour');
  const [satLatitude, setSatLatitude] = useState('');
  const [satLongitude, setSatLongitude] = useState('');
  const [satEpsg, setSatEpsg] = useState('');
  const [satDrawingUnit, setSatDrawingUnit] = useState('meters');
  const [satelliteResults, setSatelliteResults] = useState<any[]>([]);
  const [selectedSatellite, setSelectedSatellite] = useState<any | null>(null);
  const [satelliteSearching, setSatelliteSearching] = useState(false);
  const [satelliteError, setSatelliteError] = useState('');

  // Step 3 (CSV): CSV files carry no unit/CRS metadata, so the user supplies them.
  // Pre-filled from the validation response (derived from the filename) and editable.
  const [csvUnit, setCsvUnit] = useState<'SI' | 'Imperial'>('SI');
  const [csvEpsg, setCsvEpsg] = useState('');

  // Step 3 (XLSX): drawing unit, EPSG, and per-area selection. Mirrors the
  // Angular xlsx-upload modal — pre-filled from validation_data, user edits
  // EPSG/unit and ticks which PV areas to include in the extraction.
  const [xlsxUnit, setXlsxUnit] = useState<'SI' | 'Imperial'>('SI');
  const [xlsxEpsg, setXlsxEpsg] = useState('');
  const [xlsxAreas, setXlsxAreas] = useState<any[]>([]);
  const [xlsxSelectedRows, setXlsxSelectedRows] = useState<number[]>([]);
  const [xlsxPvModuleInfo, setXlsxPvModuleInfo] = useState<any>(null);
  const [xlsxRackInfo, setXlsxRackInfo] = useState<any[]>([]);

  // Unit detected from uploaded file (SI or Imperial)
  const [sourceUnit, setSourceUnit] = useState<'SI' | 'Imperial'>('SI');
  const revealUnit = sourceUnit === 'Imperial' ? 'in' : 'mm';
  const lengthUnit = sourceUnit === 'Imperial' ? 'ft' : 'm';

  // Configuration parameters. Defaults mirror the Terrain Pro app's Configuration 1
  // baseline (SI: 1 m pile reveal, 300 mm reveal window, 8.5° slopes, 0° slope change).
  const [configParams, setConfigParams] = useState({
    min_pile_reveal: 1,
    max_slope_north: 8.5,
    max_slope_south: 8.5,
    reveal_window: 300,
    max_articulation_angle: 0,
    algorithm_version: 6.3,
    // Grading objective forwarded to the solver. Only meaningful when Solution Four
    // runs (reveal_window > 0 && max_articulation_angle > 0). Matches the Terrain Pro
    // app's "Minimize earthwork" / "Minimize pile reveal" toggle; defaults to earthwork.
    optimization_goal: 'earthwork' as 'earthwork' | 'pileReveal',
  });

  // Resource Explorer: user-supplied IDs for detail/sub-resource calls
  const [resourceProjectId, setResourceProjectId] = useState('');
  const [resourceLayoutId, setResourceLayoutId] = useState('');
  const [resourceJobId, setResourceJobId] = useState('');
  const [resourceRunJobId, setResourceRunJobId] = useState('');
  const [resourceConfigOutputsId, setResourceConfigOutputsId] = useState('');
  const [resourceConfigLayersId, setResourceConfigLayersId] = useState('');

  // For Step 4: Run mode toggle and batch parameters
  const [runMode, setRunMode] = useState<'default' | 'single' | 'batch'>('default');
  // Selected tracker-vendor preset ('Custom' = manual values).
  const [selectedPreset, setSelectedPreset] = useState<string>('Custom');
  const [batchParams, setBatchParams] = useState({
    reveal_windows: '100, 200, 300',
    max_articulation_angles: '0, 0.75, 1.5',
  });
  const [batchConfigs, setBatchConfigs] = useState<any[]>([]);
  const [existingConfig, setExistingConfig] = useState<any | null>(null);
  const [existingConfigLoading, setExistingConfigLoading] = useState(false);

  const [resourceExplorer, setResourceExplorer] = useState<{
    loading: boolean;
    data: {
      projects?: any;
      layouts?: any;
      configurations?: any;
      jobs?: any;
      projectDetail?: any;
      layoutDetail?: any;
      layoutRuns?: any;
      jobDetail?: any;
      runDetail?: any;
      configOutputs?: any;
      configLayers?: any;
    } | null;
    error: string | null;
  }>({ loading: false, data: null, error: null });
  // Step 5: optional map-layer creation (texture/elevation/terrarium PNGs + manifest)
  const [layerState, setLayerState] = useState<{
    status: 'idle' | 'running' | 'success' | 'error';
    layers: any[];
    progress: number;
    completed: number;
    total: number;
    error: string | null;
  }>({ status: 'idle', layers: [], progress: 0, completed: 0, total: 0, error: null });
  const layerPollingRef = React.useRef<NodeJS.Timeout | null>(null);

  const pollingIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (layerPollingRef.current) {
        clearInterval(layerPollingRef.current);
      }
    };
  }, []);

  // Close project dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(e.target as Node)) {
        setProjectDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredProjects = projectsList.filter(p => {
    if (!projectSearch.trim()) return true;
    const term = projectSearch.toLowerCase();
    return (p.project_name || '').toLowerCase().includes(term);
  });

  const handleProjectSelect = useCallback((proj: any) => {
    setSelectedProjectId(proj.id);
    setProjectName(proj.project_name);
    setProjectSearch('');
    setProjectDropdownOpen(false);
  }, []);

  const reverseGeocode = useCallback((lat: string, lon: string) => {
    if (geoTimerRef.current) clearTimeout(geoTimerRef.current);
    const latNum = parseFloat(lat);
    const lonNum = parseFloat(lon);
    if (isNaN(latNum) || isNaN(lonNum) || latNum < -90 || latNum > 90 || lonNum < -180 || lonNum > 180) {
      setGeoInfo(null);
      return;
    }
    if (!authToken) {
      setGeoInfo(null);
      return;
    }
    geoTimerRef.current = setTimeout(async () => {
      setGeoLoading(true);
      try {
        const resp = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/geo-info`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ lat: latNum, lon: lonNum }),
        });

        if (resp.ok) {
          const data = await resp.json();
          const info: { city?: string; state?: string; country?: string; elevation?: number; timezone?: number } = {};

          if (data.location) {
            info.city = data.location.city || data.location.locality || '';
            info.state = data.location.state || data.location.region || '';
            info.country = data.location.country || data.location.country_code || '';
          }
          if (data.elevation != null) {
            const elev = typeof data.elevation === 'object' ? (data.elevation.elevation ?? data.elevation.elevation_in_meters) : data.elevation;
            if (elev != null) info.elevation = elev;
          }
          if (data.timezone != null) {
            const tz = typeof data.timezone === 'object' ? (data.timezone.utc_offset ?? data.timezone.offset ?? data.timezone.timezone) : data.timezone;
            if (tz != null) info.timezone = tz;
          }

          setGeoInfo(Object.keys(info).length > 0 ? info : null);
        } else {
          setGeoInfo(null);
        }
      } catch {
        setGeoInfo(null);
      } finally {
        setGeoLoading(false);
      }
    }, 600);
  }, [authToken]);

  // Apply a tracker-vendor preset's values for the given unit to the editable params.
  const applyVendorPreset = useCallback((name: string, unit: 'SI' | 'Imperial') => {
    const preset = VENDOR_PRESETS[name];
    if (!preset) return;
    const mpr = preset.min_pile_reveal[unit];
    const rw = preset.reveal_window[unit];
    setConfigParams(prev => ({
      ...prev,
      min_pile_reveal: mpr,
      reveal_window: rw,
      max_slope_north: preset.max_slope_north,
      max_slope_south: preset.max_slope_south,
      max_articulation_angle: preset.max_articulation_angle,
    }));
    setBatchParams(prev => ({
      ...prev,
      reveal_windows: String(rw),
      max_articulation_angles: String(preset.max_articulation_angle),
    }));
  }, []);

  // Keep parameters in sync with the selected preset and unit. When a vendor preset
  // is selected, its per-unit values drive the fields; otherwise fall back to the
  // generic per-unit defaults. Re-runs when the unit or the selected preset changes.
  useEffect(() => {
    if (selectedPreset !== 'Custom' && VENDOR_PRESETS[selectedPreset]) {
      applyVendorPreset(selectedPreset, sourceUnit);
      return;
    }
    if (sourceUnit === 'Imperial') {
      setConfigParams(prev => ({
        ...prev,
        min_pile_reveal: 3,
        max_slope_north: 8.5,
        max_slope_south: 8.5,
        reveal_window: 12,
        max_articulation_angle: 0,
      }));
      setBatchParams(prev => ({
        ...prev,
        reveal_windows: '12, 18, 24',
      }));
    } else {
      setConfigParams(prev => ({
        ...prev,
        min_pile_reveal: 1,
        max_slope_north: 8.5,
        max_slope_south: 8.5,
        reveal_window: 300,
        max_articulation_angle: 0,
      }));
      setBatchParams(prev => ({
        ...prev,
        reveal_windows: '300, 450, 600',
      }));
    }
  }, [sourceUnit, selectedPreset, applyVendorPreset]);

  // Fetch existing configuration details when Step 4 unlocks
  useEffect(() => {
    const step4 = steps[3];
    if (step4.status !== 'ready' || existingConfig || existingConfigLoading) return;

    const configId =
      steps[2].responseData?.configuration_id ||
      steps[1].responseData?.configuration_id;
    if (!configId || !authToken) return;

    setExistingConfigLoading(true);
    fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/configurations/${configId}/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    })
      .then(res => res.json())
      .then(data => {
        setExistingConfig(data);
        setExistingConfigLoading(false);
      })
      .catch(() => {
        setExistingConfigLoading(false);
      });
  }, [steps, authToken, existingConfig, existingConfigLoading]);

  const getStepInfo = (stepNumber: number) => {
    const stepConfig = {
      1: { icon: '🔐', title: 'Authenticate', description: 'Verify JWT token and get user info', apiPath: '/tools/api/v1/terrain-pro/auth-info' },
      2: { icon: '📁', title: 'Validate File', description: 'Upload and validate terrain file', apiPath: '/tools/api/v1/terrain-pro/validate' },
      3: { icon: '⚙️', title: 'Extract Data', description: 'Extract pile locations and elevation data', apiPath: '/tools/api/v1/terrain-pro/extraction' },
      4: { icon: '🚀', title: 'Run Analysis', description: 'Run the default configuration, create a new one, or run a batch', apiPath: runMode === 'batch' ? '/tools/api/v1/terrain-pro/layouts/{layout_id}/runs/batch' : runMode === 'default' ? '/tools/api/v1/terrain-pro/configurations/{config_id}/run' : '/tools/api/v1/terrain-pro/layouts/{layout_id}/run' },
      5: { icon: '📥', title: 'Download Results', description: 'Retrieve generated files', apiPath: '/tools/api/v1/terrain-pro/configurations/{config_id}/outputs' },
    };
    return stepConfig[stepNumber as keyof typeof stepConfig];
  };

  const updateStepStatus = (stepNumber: number, updates: Partial<StepState>) => {
    setSteps(prev => prev.map(step => {
      if (step.stepNumber === stepNumber) {
        if (stepNumber === 5 && prev[3]?.status === 'success' && step.responseData && !updates.responseData) {
          return { ...step, ...updates, responseData: step.responseData };
        }
        return { ...step, ...updates };
      }
      return step;
    }));
  };

  const unlockNextStep = (stepNumber: number) => {
    if (stepNumber < 5) {
      setSteps(prev => prev.map(step => {
        if (step.stepNumber === stepNumber + 1) {
          return { ...step, status: 'ready' as const };
        }
        return step;
      }));
      setCurrentStep(stepNumber + 1);
      
      setTimeout(() => {
        const nextStepEl = document.getElementById(`step-${stepNumber + 1}`);
        if (nextStepEl) {
          nextStepEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  };

  const pollJobStatus = async (jobId: string, stepNumber: number, jobType: 'validation' | 'extraction' | 'analysis', initialData?: any) => {
    let attempts = 0;
    const maxAttempts = 60;

    await new Promise(resolve => setTimeout(resolve, 5000));

    pollingIntervalRef.current = setInterval(async () => {
      attempts++;

      if (attempts > maxAttempts) {
        clearInterval(pollingIntervalRef.current!);
        pollingIntervalRef.current = null;
        updateStepStatus(stepNumber, {
          status: 'error',
          errorMessage: 'Job timeout: exceeded maximum wait time',
          endTime: new Date()
        });
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/job/${jobId}/status`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          }
        });

        const statusData = await response.json();

        const progress = statusData.progress || 0;
        if (stepNumber === 3) {
          setExtractionProgress(progress);
        }

        updateStepStatus(stepNumber, {
          statusMessage: statusData.message || `Status: ${statusData.status} (${progress}%)`,
        });

        if (statusData.status === 'completed' || statusData.status === 'success') {
          if (stepNumber === 3) setExtractionProgress(0);
          clearInterval(pollingIntervalRef.current!);
          pollingIntervalRef.current = null;
          
          let fullData;
          if (jobType === 'validation') {
            const dataResponse = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/validate/${jobId}/data`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
            });
            fullData = await dataResponse.json();
            
            if (initialData?.layout_id && !fullData.layout_id) {
              fullData.layout_id = initialData.layout_id;
            }
          } else if (jobType === 'extraction') {
            const dataResponse = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/extraction/${jobId}/data`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
            });
            fullData = await dataResponse.json();
          } else {
            fullData = statusData;
          }

          updateStepStatus(stepNumber, {
            status: 'success',
            responseData: fullData,
            endTime: new Date(),
            statusMessage: 'Completed successfully'
          });

          if (stepNumber === 2) {
            if (fullData.source_unit || initialData?.source_unit) {
              setSourceUnit((fullData.source_unit || initialData.source_unit) as 'SI' | 'Imperial');
            }
          }

          if (stepNumber === 2 && fullData.validation_data) {
            // Pre-fill satellite search fields from validation output
            const valCoordSys = fullData.validation_data.coordinateSystem;
            if (valCoordSys?.epsg && valCoordSys.epsg !== 'Not Found') {
              setSatEpsg(String(valCoordSys.epsg));
            }
            if (fullData.validation_data.latitude != null) {
              setSatLatitude(String(parseFloat(fullData.validation_data.latitude).toFixed(4)));
            }
            if (fullData.validation_data.longitude != null) {
              setSatLongitude(String(parseFloat(fullData.validation_data.longitude).toFixed(4)));
            }
            const drawUnit = fullData.validation_data.drawingUnit;
            if (drawUnit) {
              setSatDrawingUnit(drawUnit === 'Meters' ? 'meters' : 'feet');
            }

            // CSV: pre-fill the editable Unit / EPSG fields from the values the
            // backend derived from the filename. The user can still override.
            if (fullData.file_type === 'CSV') {
              if (drawUnit) {
                setCsvUnit(drawUnit === 'Meters' || drawUnit === 'SI' ? 'SI' : 'Imperial');
              }
              if (valCoordSys?.epsg && valCoordSys.epsg !== 'Not Found') {
                setCsvEpsg(String(valCoordSys.epsg));
              }
            }

            // XLSX: pre-fill unit/EPSG from validation and populate the
            // PV-area / rack / module tables for the Step 3 selection UI.
            if (fullData.file_type === 'XLSX') {
              if (drawUnit) {
                setXlsxUnit(drawUnit === 'Meters' || drawUnit === 'SI' ? 'SI' : 'Imperial');
              }
              if (valCoordSys?.epsg && valCoordSys.epsg !== 'Not Found') {
                setXlsxEpsg(String(valCoordSys.epsg));
              }
              const areas = fullData.validation_data.information_area;
              if (Array.isArray(areas)) {
                setXlsxAreas(areas);
                setXlsxSelectedRows(areas.map((a: any) => a.row));
              }
              setXlsxPvModuleInfo(fullData.validation_data.pv_module_info || null);
              setXlsxRackInfo(Array.isArray(fullData.validation_data.rack_info) ? fullData.validation_data.rack_info : []);
            }

            if (fullData.validation_data.elevationLayer && Array.isArray(fullData.validation_data.elevationLayer)) {
              const layerNames = fullData.validation_data.elevationLayer
                .map((layer: any) => typeof layer === 'string' ? layer : layer.layerName || '')
                .filter((name: string) => name !== '');
              setAvailableLayers(layerNames);
              setSelectedLayers(layerNames);
            }
            
            if (fullData.validation_data.nestedBlocks && Array.isArray(fullData.validation_data.nestedBlocks)) {
              const blockNames = fullData.validation_data.nestedBlocks
                .map((block: any) => typeof block === 'string' ? block : block.rackBlockName || block.blockName || '')
                .filter((name: string) => name !== '');
              setAvailableBlocks(blockNames);
              setSelectedBlocks(blockNames);

              const nestedNames = new Set<string>();
              fullData.validation_data.nestedBlocks.forEach((block: any) => {
                if (block.nestedBlocks && Array.isArray(block.nestedBlocks)) {
                  block.nestedBlocks.forEach((nested: any) => {
                    const name = nested.nestedBlockName || nested.name;
                    if (name) nestedNames.add(name);
                  });
                }
              });
              const uniqueNested = Array.from(nestedNames);
              setAvailableNestedBlocks(uniqueNested);
              setSelectedNestedBlocks(uniqueNested);
            }
          }

          unlockNextStep(stepNumber);
        } else if (statusData.status === 'failed' || statusData.status === 'error') {
          clearInterval(pollingIntervalRef.current!);
          pollingIntervalRef.current = null;
          updateStepStatus(stepNumber, {
            status: 'error',
            errorMessage: statusData.error || 'Job failed',
            endTime: new Date()
          });
        }
      } catch (error: any) {
        clearInterval(pollingIntervalRef.current!);
        pollingIntervalRef.current = null;
        updateStepStatus(stepNumber, {
          status: 'error',
          errorMessage: error.message || 'Error checking job status',
          endTime: new Date()
        });
      }
    }, 5000);
  };

  // initialResponseData is captured by value at call time, avoiding stale closure reads
  // of the `steps` state that would lose fields like `configuration.config_id`.
  const pollMultipleJobs = async (jobs: any[], stepNumber: number, initialResponseData: Record<string, any> = {}) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        let completedCount = 0;
        let failedCount = 0;
        const updatedJobs: any[] = [];

        for (const job of jobs) {
          const statusResponse = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/job/${job.job_id}/status`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            }
          });

          const statusData = await statusResponse.json();
          
          updatedJobs.push({
            job_id: job.job_id,
            job_type: job.job_type,
            status: statusData.status,
            progress: statusData.progress || 0
          });

          if (statusData.status === 'completed') completedCount++;
          else if (statusData.status === 'failed' || statusData.status === 'error') failedCount++;
        }

        const totalJobs = jobs.length;
        // Merge updated jobs into the original API response (not stale closure state)
        // so fields like `configuration.config_id` are always preserved.
        const mergedData = { ...initialResponseData, jobs: updatedJobs };

        updateStepStatus(stepNumber, {
          responseData: mergedData,
          statusMessage: `${completedCount} of ${totalJobs} completed${failedCount > 0 ? `, ${failedCount} failed` : ''}`
        });

        if (completedCount + failedCount === totalJobs) {
          clearInterval(pollingIntervalRef.current!);
          pollingIntervalRef.current = null;
          
          if (failedCount > 0) {
            updateStepStatus(stepNumber, {
              status: 'error',
              responseData: mergedData,
              errorMessage: `${failedCount} of ${totalJobs} jobs failed`,
              endTime: new Date()
            });
          } else {
            updateStepStatus(stepNumber, {
              status: 'success',
              responseData: mergedData,
              endTime: new Date(),
              statusMessage: `All ${totalJobs} jobs completed`
            });
            unlockNextStep(stepNumber);
          }
        }
      } catch (error: any) {
        clearInterval(pollingIntervalRef.current!);
        pollingIntervalRef.current = null;
        updateStepStatus(stepNumber, {
          status: 'error',
          errorMessage: error.message || 'Error checking jobs status',
          endTime: new Date()
        });
      }
    }, 3000);
  };

  const pollBatchJobs = async (configurations: any[], stepNumber: number) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        let totalCompleted = 0;
        let totalFailed = 0;
        let totalJobs = 0;
        const updatedConfigs: any[] = [];

        for (const config of configurations) {
          const updatedJobs: any[] = [];
          for (const job of (config.jobs || [])) {
            totalJobs++;
            const statusResponse = await fetch(
              `${API_BASE_URL}/tools/api/v1/terrain-pro/job/${job.job_id}/status`,
              {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${authToken}`,
                  'Content-Type': 'application/json',
                }
              }
            );
            const statusData = await statusResponse.json();
            updatedJobs.push({
              ...job,
              status: statusData.status,
              progress: statusData.progress || 0,
            });
            if (statusData.status === 'completed') totalCompleted++;
            else if (statusData.status === 'failed' || statusData.status === 'error') totalFailed++;
          }
          updatedConfigs.push({ ...config, jobs: updatedJobs });
        }

        const step = steps[stepNumber - 1];
        setBatchConfigs(updatedConfigs);

        updateStepStatus(stepNumber, {
          responseData: { ...step.responseData, configurations: updatedConfigs },
          statusMessage: `${totalCompleted} of ${totalJobs} jobs completed${totalFailed > 0 ? `, ${totalFailed} failed` : ''}`
        });

        if (totalCompleted + totalFailed === totalJobs) {
          clearInterval(pollingIntervalRef.current!);
          pollingIntervalRef.current = null;

          if (totalFailed > 0) {
            updateStepStatus(stepNumber, {
              status: 'error',
              responseData: { ...step.responseData, configurations: updatedConfigs },
              errorMessage: `${totalFailed} of ${totalJobs} jobs failed`,
              endTime: new Date()
            });
          } else {
            updateStepStatus(stepNumber, {
              status: 'success',
              responseData: { ...step.responseData, configurations: updatedConfigs },
              endTime: new Date(),
              statusMessage: `All ${totalJobs} jobs across ${updatedConfigs.length} configurations completed`
            });
            unlockNextStep(stepNumber);
          }
        }
      } catch (error: any) {
        clearInterval(pollingIntervalRef.current!);
        pollingIntervalRef.current = null;
        updateStepStatus(stepNumber, {
          status: 'error',
          errorMessage: error.message || 'Error checking batch jobs status',
          endTime: new Date()
        });
      }
    }, 3000);
  };

  const handleSatelliteSearch = async () => {
    const lat = parseFloat(satLatitude);
    const lon = parseFloat(satLongitude);
    if (isNaN(lat) || isNaN(lon)) {
      setSatelliteError('Please provide valid latitude and longitude.');
      return;
    }
    setSatelliteSearching(true);
    setSatelliteError('');
    setSatelliteResults([]);
    setSelectedSatellite(null);

    const epsgStr = satEpsg.trim();

    // Try the terrain-pro-api proxy first; fall back to the gen1 endpoint.
    const endpoints = [
      {
        url: `${API_BASE_URL}/tools/api/v1/terrain-pro/satellite-search`,
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ latitude: lat, longitude: lon, epsg: epsgStr, drawing_unit: satDrawingUnit }),
      },
      {
        url: `${API_BASE_URL}/gen1/v1/get-elevation-data-for-points`,
        headers: { 'Authorization': `Bearer-PP ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coordinates: [[lon, lat]],
          drawing_unit: satDrawingUnit === 'meters' ? 'meters' : 'feet',
          epsg_num: epsgStr,
          is_lat_lon_coordinate: true,
        }),
      },
    ];

    for (const ep of endpoints) {
      try {
        const response = await fetch(ep.url, {
          method: 'POST',
          headers: ep.headers,
          body: ep.body,
        });

        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          continue; // HTML error page — try next endpoint
        }

        const data = await response.json();
        if (response.ok && data.elevation_details && data.elevation_details.length > 0) {
          const sorted = [...data.elevation_details].sort((a: any, b: any) => {
            if (a.pixelSize !== b.pixelSize) return a.pixelSize - b.pixelSize;
            return (a.productName || '').localeCompare(b.productName || '');
          });
          setSatelliteResults(sorted);
          setSatelliteSearching(false);
          return;
        } else if (response.ok) {
          setSatelliteError('No satellite data available for this location. Verify latitude, longitude, and EPSG.');
          setSatelliteSearching(false);
          return;
        }
        // Non-OK JSON response — try next endpoint
      } catch {
        // Network error or JSON parse failure — try next
      }
    }

    setSatelliteError('Satellite search is not available. Please ensure the backend is deployed with the satellite-search endpoint.');
    setSatelliteSearching(false);
  };

  const handleExecuteStep = async (stepNumber: number) => {
    const step = steps[stepNumber - 1];
    
    if (step.status === 'locked') {
      alert('Please complete previous steps first');
      return;
    }

    if (step.status === 'running') return;

    if (!authToken) {
      alert('Please authenticate first by entering your JWT token in the header');
      return;
    }

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    updateStepStatus(stepNumber, { 
      status: 'running', 
      startTime: new Date(),
      errorMessage: null,
      statusMessage: 'Initializing...'
    });

    try {
      let data;
      let layout_id;

      switch (stepNumber) {
        case 1: { // Authenticate
          updateStepStatus(1, {
            statusMessage: 'Verifying token...',
            requestData: {
              method: 'GET',
              url: `${API_BASE_URL}/tools/api/v1/terrain-pro/auth-info`,
              headers: { Authorization: `Bearer ${authToken.substring(0, 20)}...` }
            }
          });
          const response = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/auth-info`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
          });
          data = await response.json();
          
          if (response.ok) {
            setLocalUserInfo({
              user_email: data.user || '',
              user_uuid: data.cognito_uuid || ''
            });
            
            updateStepStatus(1, {
              status: 'success',
              responseData: data,
              endTime: new Date(),
              statusMessage: 'Authentication successful'
            });
            unlockNextStep(1);

            // Fetch projects list for the dropdown in Step 2
            setProjectsLoading(true);
            try {
              const projResp = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/my-projects`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${authToken}`,
                  'Content-Type': 'application/json',
                },
              });
              if (projResp.ok) {
                const projData = await projResp.json();
                const results = projData.results || projData;
                setProjectsList(Array.isArray(results) ? results : []);
                if (Array.isArray(results) && results.length > 0) {
                  setProjectMode('existing');
                }
              }
            } catch {
              // Non-blocking: projects dropdown will fall back to manual entry
            } finally {
              setProjectsLoading(false);
            }
          } else {
            throw new Error(`Authentication failed: ${JSON.stringify(data)}`);
          }
          break;
        }

        case 2: { // Validate File (with upload progress)
          if (!selectedFile) {
            throw new Error('Please select a file first');
          }
          if (!localUserInfo || !localUserInfo.user_uuid || !localUserInfo.user_email) {
            throw new Error('User information not available. Please complete Step 1 (Authentication) first.');
          }

          const fileType = selectedFile.name.split('.').pop()?.toUpperCase() || 'DXF';
          const projectPayload: Record<string, string> = {};
          if (projectMode === 'existing' && selectedProjectId) {
            projectPayload.project_id = String(selectedProjectId);
          } else {
            projectPayload.project_name = projectName;
            if (projectLat) projectPayload.latitude = projectLat;
            if (projectLon) projectPayload.longitude = projectLon;
            if (geoInfo?.city) projectPayload.city = geoInfo.city;
            if (geoInfo?.state) projectPayload.state = geoInfo.state;
          }

          updateStepStatus(2, {
            statusMessage: 'Uploading file... 0%',
            requestData: {
              method: 'POST',
              url: `${API_BASE_URL}/tools/api/v1/terrain-pro/validate`,
              body: {
                file: `${selectedFile.name} (${(selectedFile.size / 1024).toFixed(1)} KB)`,
                file_type: fileType,
                user_uuid: localUserInfo.user_uuid,
                user_email: localUserInfo.user_email,
                ...projectPayload,
                layout_name: layoutName
              }
            }
          });

          const formData = new FormData();
          formData.append('file', selectedFile);
          formData.append('file_type', fileType);
          formData.append('user_uuid', localUserInfo.user_uuid);
          formData.append('user_email', localUserInfo.user_email);
          if (projectMode === 'existing' && selectedProjectId) {
            formData.append('project_id', String(selectedProjectId));
          } else {
            formData.append('project_name', projectName);
            if (projectLat) formData.append('latitude', projectLat);
            if (projectLon) formData.append('longitude', projectLon);
            if (geoInfo?.city) formData.append('city', geoInfo.city);
            if (geoInfo?.state) formData.append('state', geoInfo.state);
          }
          formData.append('layout_name', layoutName);

          data = await new Promise<any>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.upload.onprogress = (event) => {
              if (event.lengthComputable) {
                const pct = Math.round((event.loaded / event.total) * 100);
                setUploadProgress(pct);
                updateStepStatus(2, { statusMessage: `Uploading file... ${pct}%` });
              }
            };
            xhr.onload = () => {
              setUploadProgress(0);
              try {
                const json = JSON.parse(xhr.responseText);
                if (xhr.status >= 200 && xhr.status < 300) {
                  resolve(json);
                } else {
                  reject(new Error(json.error || 'File validation failed'));
                }
              } catch {
                reject(new Error('Invalid server response'));
              }
            };
            xhr.onerror = () => {
              setUploadProgress(0);
              reject(new Error('Network error during upload'));
            };
            xhr.open('POST', `${API_BASE_URL}/tools/api/v1/terrain-pro/validate`);
            xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
            xhr.send(formData);
          });

          layout_id = data.layout_id;

          if (data.source_unit) {
            setSourceUnit(data.source_unit as 'SI' | 'Imperial');
          }

          updateStepStatus(2, { statusMessage: 'File uploaded, validating...' });
          
          if (data.job_id) {
            await pollJobStatus(data.job_id, 2, 'validation', data);
          } else {
            updateStepStatus(2, {
              status: 'success',
              responseData: { ...data, layout_id: data.layout_id || layout_id },
              endTime: new Date()
            });
            unlockNextStep(2);
          }
          break;
        }

        case 3: { // Extract Data
          const validationData = steps[1].responseData;
          if (!validationData || !validationData.job_id) {
            throw new Error('Validation data not available');
          }
          if (!localUserInfo || !localUserInfo.user_uuid || !localUserInfo.user_email) {
            throw new Error('User information not available. Please complete Step 1 (Authentication) first.');
          }

          setExtractionProgress(0);
          updateStepStatus(3, { statusMessage: 'Extracting data (this may take a moment)...' });

          const validationInfo = validationData.validation_data || {};
          const unit = validationInfo.drawingUnit || 'SI';
          const epsg = validationInfo.coordinateSystem?.epsg || 32610;

          const useSatellite = elevationSource === 'satellite' && selectedSatellite !== null;

          const extractionPayload: any = {
            job_id: validationData.job_id,
            file_type: validationData.file_type || 'DXF',
            user_uuid: localUserInfo.user_uuid,
            user_email: localUserInfo.user_email,
            configuration_id: validationData.configuration_id,
            file_path: validationData.file_path,
            unit: unit,
            epsg: useSatellite && satEpsg.trim() ? parseInt(satEpsg.trim(), 10) || epsg : epsg,
            is_satellite_data: useSatellite,
            ...(useSatellite && { satellite_data: selectedSatellite }),
            algorithm_version: 6.3,
            filtering_values: {
              default_weeding_distance: 10,
              default_weeding_angle: 10,
              default_supplementing_distance: 100,
              default_mid_ord_distance: 0.1
            }
          };

          if (validationData.file_type === 'DXF') {
            const layersToUse = selectedLayers.length > 0 ? selectedLayers : availableLayers;
            const blocksToUse = selectedBlocks.length > 0 ? selectedBlocks : availableBlocks;
            
            if (useSatellite) {
              if (layersToUse.length > 0) {
                extractionPayload.layers = layersToUse;
              }
            } else if (layersToUse.length > 0) {
              extractionPayload.layers = layersToUse;
            } else {
              throw new Error('No layers selected for DXF extraction. Please select at least one layer.');
            }
            
            if (blocksToUse.length > 0) {
              extractionPayload.checked_blocks = blocksToUse;
            }
            
            const nestedBlocksToUse = selectedNestedBlocks.length > 0 ? selectedNestedBlocks : availableNestedBlocks;
            if (nestedBlocksToUse.length > 0) {
              extractionPayload.checked_nested_blocks = nestedBlocksToUse;
            }
          }

          if (validationData.file_type === 'XLSX') {
            if (xlsxSelectedRows.length === 0) {
              throw new Error('Select at least one PV area to extract.');
            }
            extractionPayload.checked_areas_rows = xlsxSelectedRows;
            extractionPayload.unit = xlsxUnit;
            const parsedXlsxEpsg = parseInt(xlsxEpsg.trim(), 10);
            if (!Number.isNaN(parsedXlsxEpsg)) {
              extractionPayload.epsg = parsedXlsxEpsg;
            }
          }

          if (validationData.file_type === 'CSV') {
            const mappingData = validationInfo.mapping_data;
            if (mappingData) {
              extractionPayload.mapping_data = mappingData;
            } else {
              console.warn('No mapping_data found in validation data for CSV file');
            }
            // CSV carries no unit/CRS metadata, so use the user-supplied (and
            // filename-prefilled) values instead of the DXF auto-derive fallback.
            extractionPayload.unit = csvUnit;
            const parsedCsvEpsg = parseInt(csvEpsg.trim(), 10);
            if (!Number.isNaN(parsedCsvEpsg)) {
              extractionPayload.epsg = parsedCsvEpsg;
            }
          }

          updateStepStatus(3, {
            statusMessage: 'Extracting data (this may take a moment)...',
            requestData: {
              method: 'POST',
              url: `${API_BASE_URL}/tools/api/v1/terrain-pro/extraction`,
              body: extractionPayload
            }
          });

          const extractResponse = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/extraction`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(extractionPayload),
          });

          data = await extractResponse.json();

          if (extractResponse.ok) {
            if (data.status === 'completed' || data.status === 'success') {
              updateStepStatus(3, {
                status: 'success',
                responseData: data,
                endTime: new Date(),
                statusMessage: 'Extraction completed'
              });
              unlockNextStep(3);
            } else if (data.job_id) {
              updateStepStatus(3, { statusMessage: 'Extracting data...' });
              await pollJobStatus(data.job_id, 3, 'extraction');
            } else {
              updateStepStatus(3, {
                status: 'success',
                responseData: data,
                endTime: new Date()
              });
              unlockNextStep(3);
            }
          } else {
            throw new Error(data.error || 'Data extraction failed');
          }
          break;
        }

        case 4: { // Run Analysis
          if (!localUserInfo || !localUserInfo.user_uuid || !localUserInfo.user_email) {
            throw new Error('User information not available. Please complete Step 1 (Authentication) first.');
          }

          if (runMode === 'default') {
            // Run the default configuration created during extraction
            if (!existingConfig || !existingConfig.id) {
              throw new Error('No default configuration found. Please create a new configuration instead.');
            }

            const configId = existingConfig.id;

            updateStepStatus(4, {
              statusMessage: `Running default ${existingConfig.configuration_name || 'Configuration 1'}...`,
              requestData: {
                method: 'POST',
                url: `${API_BASE_URL}/tools/api/v1/terrain-pro/configurations/${configId}/run`,
                body: {
                  user_uuid: localUserInfo.user_uuid,
                  user_email: localUserInfo.user_email,
                  optimization_goal: configParams.optimization_goal,
                }
              }
            });

            const runResponse = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/configurations/${configId}/run`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                user_uuid: localUserInfo.user_uuid,
                user_email: localUserInfo.user_email,
                optimization_goal: configParams.optimization_goal,
              }),
            });

            data = await runResponse.json();

            if (runResponse.ok) {
              updateStepStatus(4, { statusMessage: 'Analysis running...', responseData: data });

              let jobsToPoll: any[] = Array.isArray(data.jobs) ? [...data.jobs] : [];
              if ((data.sol1_status === 'created' || data.sol1_status === 'running') && data.sol1_job_id) {
                jobsToPoll = [
                  { job_id: data.sol1_job_id, job_type: 'solution_one', status: 'queued', progress: 0 },
                  ...jobsToPoll
                ];
              }

              if (jobsToPoll.length > 0) {
                await pollMultipleJobs(jobsToPoll, 4, data);
              } else {
                updateStepStatus(4, { status: 'success', responseData: data, endTime: new Date() });
                unlockNextStep(4);
              }
            } else {
              throw new Error(data.error || 'Failed to run default configuration');
            }
          } else if (runMode === 'batch') {
            const extractionDataForBatch = steps[2].responseData;
            const validationDataForBatch = steps[1].responseData;
            const batchLayoutId = extractionDataForBatch?.layout_id || validationDataForBatch?.layout_id;

            if (!batchLayoutId) {
              throw new Error('Layout ID not available. Please ensure previous steps completed successfully.');
            }

            const parsedRevealWindows = batchParams.reveal_windows
              .split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v));
            const parsedAngles = batchParams.max_articulation_angles
              .split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v));

            if (parsedRevealWindows.length === 0) {
              throw new Error('Please provide at least one reveal window value.');
            }
            if (parsedAngles.length === 0) {
              throw new Error('Please provide at least one max articulation angle value.');
            }

            const batchPayload = {
              min_pile_reveal: configParams.min_pile_reveal,
              max_slope_north: configParams.max_slope_north,
              max_slope_south: configParams.max_slope_south,
              reveal_windows: parsedRevealWindows,
              max_articulation_angles: parsedAngles,
              user_uuid: localUserInfo.user_uuid,
              user_email: localUserInfo.user_email,
              algorithm_version: configParams.algorithm_version,
              optimization_goal: configParams.optimization_goal,
            };

            updateStepStatus(4, {
              statusMessage: 'Starting batch analysis...',
              requestData: {
                method: 'POST',
                url: `${API_BASE_URL}/tools/api/v1/terrain-pro/layouts/${batchLayoutId}/runs/batch`,
                body: batchPayload
              }
            });

            const batchResponse = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/layouts/${batchLayoutId}/runs/batch`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(batchPayload),
            });

            data = await batchResponse.json();

            if (batchResponse.ok) {
              setBatchConfigs(data.configurations || []);
              updateStepStatus(4, { statusMessage: 'Batch analysis running...', responseData: data });

              const allConfigs = data.configurations || [];
              const hasJobs = allConfigs.some((c: any) => c.jobs && c.jobs.length > 0);

              if (hasJobs) {
                await pollBatchJobs(allConfigs, 4);
              } else {
                updateStepStatus(4, { status: 'success', responseData: data, endTime: new Date() });
                unlockNextStep(4);
              }
            } else {
              throw new Error(data.error || 'Batch analysis failed to start');
            }
          } else {
            // Single Run: POST /layouts/{layout_id}/run — passes explicit parameters so backend
            // uses the correct solution chain (Sol1 → Sol3+Sol4 for reveal>0 && angle>0).
            const extractionDataForAnalysis = steps[2].responseData;
            const validationDataForAnalysis = steps[1].responseData;

            const singleLayoutId = extractionDataForAnalysis?.layout_id || validationDataForAnalysis?.layout_id;
            if (!singleLayoutId) {
              throw new Error('Layout ID not available. Please ensure previous steps completed successfully.');
            }

            const singlePayload = {
              min_pile_reveal: configParams.min_pile_reveal,
              max_slope_north: configParams.max_slope_north,
              max_slope_south: configParams.max_slope_south,
              reveal_window: configParams.reveal_window,
              max_articulation_angle: configParams.max_articulation_angle,
              algorithm_version: configParams.algorithm_version,
              optimization_goal: configParams.optimization_goal,
              user_uuid: localUserInfo.user_uuid,
              user_email: localUserInfo.user_email,
            };

            updateStepStatus(4, {
              statusMessage: 'Starting analysis job...',
              requestData: {
                method: 'POST',
                url: `${API_BASE_URL}/tools/api/v1/terrain-pro/layouts/${singleLayoutId}/run`,
                body: singlePayload
              }
            });

            const analysisResponse = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/layouts/${singleLayoutId}/run`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(singlePayload),
            });

            data = await analysisResponse.json();

            if (analysisResponse.ok) {
              updateStepStatus(4, { statusMessage: 'Analysis running...', responseData: data });

              // Build job list to poll — always include Sol1 if it was created/is still running
              // so the user can see the full chain: Sol1 → Sol3 → Sol4.
              let jobsToPoll: any[] = Array.isArray(data.jobs) ? [...data.jobs] : [];
              if ((data.sol1_status === 'created' || data.sol1_status === 'running') && data.sol1_job_id) {
                jobsToPoll = [
                  { job_id: data.sol1_job_id, job_type: 'solution_one', status: 'queued', progress: 0 },
                  ...jobsToPoll
                ];
              }

              if (jobsToPoll.length > 0) {
                await pollMultipleJobs(jobsToPoll, 4, data);
              } else {
                // Sol1 already exists and no other jobs needed (edge case)
                updateStepStatus(4, { status: 'success', responseData: data, endTime: new Date() });
                unlockNextStep(4);
              }
            } else {
              throw new Error(data.error || 'Analysis job failed to start');
            }
          }
          break;
        }

        case 5: { // Download Results
          if (steps[3].status !== 'success') {
            throw new Error('Analysis must be completed before downloading results. Please complete Step 4 first.');
          }

          if (runMode === 'batch' && batchConfigs.length > 0) {
            updateStepStatus(5, {
              statusMessage: 'Fetching output files...',
              requestData: {
                method: 'GET',
                urls: batchConfigs.map((c: any) => `/tools/api/v1/terrain-pro/configurations/${c.config_id}/outputs`),
                note: `Fetching outputs for ${batchConfigs.length} configuration(s)`
              }
            });

            const allBatchOutputs: any[] = [];
            let totalBatchOutputs = 0;

            for (const config of batchConfigs) {
              try {
                const outputResponse = await fetch(
                  `${API_BASE_URL}/tools/api/v1/terrain-pro/configurations/${config.config_id}/outputs`,
                  {
                    method: 'GET',
                    headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
                  }
                );
                const outputData = await outputResponse.json();
                if (outputResponse.ok && outputData.outputs && outputData.outputs.length > 0) {
                  allBatchOutputs.push({
                    config_id: config.config_id,
                    parameters: config.parameters,
                    outputs: outputData.outputs,
                  });
                  totalBatchOutputs += outputData.outputs.length;
                }
              } catch (err) {
                console.warn(`Failed to fetch outputs for config ${config.config_id}:`, err);
              }
            }

            if (allBatchOutputs.length === 0) {
              throw new Error('No output files available yet. Analysis jobs may still be processing.');
            }

            updateStepStatus(5, {
              status: 'success',
              responseData: { batch_outputs: allBatchOutputs, total_outputs: totalBatchOutputs },
              endTime: new Date(),
              statusMessage: `${totalBatchOutputs} file(s) across ${allBatchOutputs.length} configurations ready for download`
            });
          } else {
            const analysisDataForOutputs = steps[3].responseData;
            const extractionDataForOutputs = steps[2].responseData;
            const validationDataForOutputs = steps[1].responseData;

            let configIdForOutputs;
            // create_single_run creates a NEW config — use that config_id for outputs.
            // Fall back to extraction/validation config_id if not present.
            if (analysisDataForOutputs?.configuration?.config_id) {
              configIdForOutputs = analysisDataForOutputs.configuration.config_id;
            } else if (extractionDataForOutputs?.configuration_id) {
              configIdForOutputs = extractionDataForOutputs.configuration_id;
            } else if (validationDataForOutputs?.configuration_id) {
              configIdForOutputs = validationDataForOutputs.configuration_id;
            } else {
              throw new Error('Configuration ID not available');
            }

            updateStepStatus(5, {
              statusMessage: 'Fetching output files...',
              requestData: {
                method: 'GET',
                url: `${API_BASE_URL}/tools/api/v1/terrain-pro/configurations/${configIdForOutputs}/outputs`,
                configuration_id: configIdForOutputs
              }
            });

            const outputResponse = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/configurations/${configIdForOutputs}/outputs`, {
              method: 'GET',
              headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
            });

            data = await outputResponse.json();

            if (outputResponse.ok) {
              if (!data.outputs || data.outputs.length === 0) {
                throw new Error('No output files available yet. Analysis jobs may still be processing.');
              }
              updateStepStatus(5, {
                status: 'success',
                responseData: data,
                endTime: new Date(),
                statusMessage: `${data.total_outputs} file(s) ready for download`
              });
            } else {
              throw new Error(data.error || 'Failed to retrieve output files');
            }
          }
          break;
        }

        default:
          throw new Error('Invalid step number');
      }
    } catch (error: any) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setUploadProgress(0);
      setExtractionProgress(0);
      updateStepStatus(stepNumber, {
        status: 'error',
        errorMessage: error.message || 'An error occurred',
        endTime: new Date()
      });
    }
  };

  const handleExploreResources = async () => {
    setResourceExplorer({ loading: true, data: null, error: null });
    try {
      const headers = { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' };
      const [projectsRes, layoutsRes, configurationsRes, jobsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/projects/`, { method: 'GET', headers }).then(r => r.json()),
        fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/layouts/`, { method: 'GET', headers }).then(r => r.json()),
        fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/configurations/`, { method: 'GET', headers }).then(r => r.json()),
        fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/jobs/`, { method: 'GET', headers }).then(r => r.json()),
      ]);
      setResourceExplorer({ loading: false, data: { projects: projectsRes, layouts: layoutsRes, configurations: configurationsRes, jobs: jobsRes }, error: null });
    } catch (err: any) {
      setResourceExplorer({ loading: false, data: null, error: err.message || 'Failed to fetch resources' });
    }
  };

  const handleFetchDetail = async (type: 'project' | 'layout' | 'job' | 'run' | 'configOutputs' | 'configLayers', id: string) => {
    if (!id.trim() || !authToken) return;
    const headers = { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' };
    try {
      if (type === 'project') {
        const d = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/projects/${id}/`, { method: 'GET', headers }).then(r => r.json());
        setResourceExplorer(prev => ({ ...prev, data: { ...prev.data, projectDetail: { _endpoint: `GET /projects/${id}/`, ...d } } }));
      } else if (type === 'layout') {
        const [detail, runs] = await Promise.all([
          fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/layouts/${id}/`, { method: 'GET', headers }).then(r => r.json()),
          fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/layouts/${id}/runs`, { method: 'GET', headers }).then(r => r.json()),
        ]);
        setResourceExplorer(prev => ({
          ...prev,
          data: {
            ...prev.data,
            layoutDetail: { _endpoint: `GET /layouts/${id}/`, ...detail },
            layoutRuns: { _endpoint: `GET /layouts/${id}/runs`, ...runs },
          },
        }));
      } else if (type === 'job') {
        const d = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/jobs/${id}/`, { method: 'GET', headers }).then(r => r.json());
        setResourceExplorer(prev => ({ ...prev, data: { ...prev.data, jobDetail: { _endpoint: `GET /jobs/${id}/`, ...d } } }));
      } else if (type === 'run') {
        const d = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/runs/${id}`, { method: 'GET', headers }).then(r => r.json());
        setResourceExplorer(prev => ({ ...prev, data: { ...prev.data, runDetail: { _endpoint: `GET /runs/${id}`, ...d } } }));
      } else if (type === 'configOutputs') {
        const d = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/configurations/${id}/outputs`, { method: 'GET', headers }).then(r => r.json());
        setResourceExplorer(prev => ({ ...prev, data: { ...prev.data, configOutputs: { _endpoint: `GET /configurations/${id}/outputs`, ...d } } }));
      } else if (type === 'configLayers') {
        const d = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/configurations/${id}/layers`, { method: 'GET', headers }).then(r => r.json());
        setResourceExplorer(prev => ({ ...prev, data: { ...prev.data, configLayers: { _endpoint: `GET /configurations/${id}/layers`, ...d } } }));
      }
    } catch {
      // detail fetch errors are non-blocking
    }
  };

   // Step 5: poll layer-creation status. Mirrors the job-status polling pattern —
  // each layer reports completed/pending with a presigned download_url once in S3.
  const pollLayerStatus = (configId: number | string) => {
    if (layerPollingRef.current) {
      clearInterval(layerPollingRef.current);
    }
    layerPollingRef.current = setInterval(async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/configurations/${configId}/layers`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        });
        const data = await resp.json();
        if (!resp.ok) {
          throw new Error(data.error || 'Failed to fetch layer status');
        }
        setLayerState(prev => ({
          ...prev,
          layers: data.layers || [],
          progress: data.progress || 0,
          completed: data.completed || 0,
          total: data.total || prev.total,
          status: data.status === 'completed' ? 'success' : 'running',
        }));
        if (data.status === 'completed') {
          clearInterval(layerPollingRef.current!);
          layerPollingRef.current = null;
        }
      } catch (err: any) {
        clearInterval(layerPollingRef.current!);
        layerPollingRef.current = null;
        setLayerState(prev => ({ ...prev, status: 'error', error: err.message || 'Error polling layer status' }));
      }
    }, 4000);
  };

  const handleCreateLayers = async (configId: number | string) => {
    if (!authToken) {
      alert('Please authenticate first by entering your JWT token in the header');
      return;
    }
    if (layerPollingRef.current) {
      clearInterval(layerPollingRef.current);
      layerPollingRef.current = null;
    }
    setLayerState({ status: 'running', layers: [], progress: 0, completed: 0, total: 0, error: null });
    try {
      const resp = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/configurations/${configId}/layers`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.error || 'Failed to trigger layer creation');
      }
      setLayerState(prev => ({ ...prev, total: data.total_layers || 0 }));
      pollLayerStatus(configId);
    } catch (err: any) {
      setLayerState({ status: 'error', layers: [], progress: 0, completed: 0, total: 0, error: err.message || 'Failed to start layer creation' });
    }};
   

  const handleEmailLogin = async () => {
    if (!emailInput.trim()) {
      setEmailStatus({ type: 'error', message: 'Please enter an email address' });
      return;
    }
    setEmailStatus({ type: 'loading', message: 'Looking up account...' });
    try {
      const response = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/email-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailInput.trim() }),
      });
      const data = await response.json();
      if (response.ok && data.token) {
        setAuthToken(data.token);
        setEmailStatus({ type: 'success', message: `Token retrieved for ${data.user}` });
      } else {
        setEmailStatus({ type: 'error', message: data.error || 'Failed to retrieve token' });
      }
    } catch (err: any) {
      setEmailStatus({ type: 'error', message: err.message || 'Network error' });
    }
  };

  const handleStartNewFile = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (layerPollingRef.current) {
      clearInterval(layerPollingRef.current);
      layerPollingRef.current = null;
    }
    setLayerState({ status: 'idle', layers: [], progress: 0, completed: 0, total: 0, error: null });

    setSteps(prev => prev.map(step => {
      if (step.stepNumber === 1) {
        return step;
      }
      if (step.stepNumber === 2) {
        return { stepNumber: 2, status: 'ready' as const, requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null };
      }
      return { ...step, status: 'locked' as const, requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null, statusMessage: undefined };
    }));
    setCurrentStep(2);

    setSelectedFile(null);
    setProjectName('Demo Project');
    setLayoutName('Demo Layout');
    setSelectedProjectId(null);
    setProjectSearch('');
    setProjectDropdownOpen(false);
    setProjectLat('');
    setProjectLon('');
    setGeoInfo(null);
    setProjectMode(projectsList.length > 0 ? 'existing' : 'new');
    setUploadProgress(0);
    setExtractionProgress(0);

    setAvailableLayers([]);
    setAvailableBlocks([]);
    setAvailableNestedBlocks([]);
    setSelectedLayers([]);
    setSelectedBlocks([]);
    setSelectedNestedBlocks([]);

    setElevationSource('contour');
    setSatLatitude('');
    setSatLongitude('');
    setSatEpsg('');
    setSatDrawingUnit('meters');
    setSatelliteResults([]);
    setSelectedSatellite(null);
    setSatelliteSearching(false);
    setSatelliteError('');

    setSourceUnit('SI');
    setRunMode('default');
    setSelectedPreset('Custom');
    setBatchConfigs([]);
    setExistingConfig(null);
    setExistingConfigLoading(false);
    setBatchParams({ reveal_windows: '300, 450, 600', max_articulation_angles: '0, 0.75, 1.5' });
    setConfigParams({
      min_pile_reveal: 1,
      max_slope_north: 8.5,
      max_slope_south: 8.5,
      reveal_window: 300,
      max_articulation_angle: 0,
      algorithm_version: 6.3,
      optimization_goal: 'earthwork',
    });

    const fileInput = document.getElementById('file-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';

    setTimeout(() => {
      const step2El = document.getElementById('step-2');
      if (step2El) {
        step2El.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleLayerToggle = (layer: string) => {
    setSelectedLayers(prev => prev.includes(layer) ? prev.filter(l => l !== layer) : [...prev, layer]);
  };

  const handleBlockToggle = (block: string) => {
    setSelectedBlocks(prev => prev.includes(block) ? prev.filter(b => b !== block) : [...prev, block]);
  };

  const handleNestedBlockToggle = (nestedBlock: string) => {
    setSelectedNestedBlocks(prev => prev.includes(nestedBlock) ? prev.filter(nb => nb !== nestedBlock) : [...prev, nestedBlock]);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'locked': return '🔒';
      case 'ready': return '▶️';
      case 'running': return '⏳';
      case 'success': return '✓';
      case 'error': return '❌';
      default: return '⚪';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'locked': return '#94a3b8';
      case 'ready': return '#2B5390';
      case 'running': return '#f39c12';
      case 'success': return '#4e8e18';
      case 'error': return '#993300';
      default: return '#94a3b8';
    }
  };

  const getJobIcon = (status: string) => {
    if (status === 'completed') return '✓';
    if (status === 'running') return '⏳';
    if (status === 'failed' || status === 'error') return '✗';
    return '○';
  };

  const getJobColor = (status: string) => {
    if (status === 'completed') return '#28a745';
    if (status === 'running') return '#ffc107';
    if (status === 'failed' || status === 'error') return '#dc3545';
    return '#6c757d';
  };

  return (
    <div className="interactive-demo">
      <div className="demo-intro">
        <h1>
          <svg className="demo-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.5 2A2.5 2.5 0 0 1 22 4.5V16a2.5 2.5 0 0 1-2.5 2.5H13l-3 3l-3-3H4.5A2.5 2.5 0 0 1 2 16V4.5A2.5 2.5 0 0 1 4.5 2h15zM12 7a1 1 0 0 0-1 1v4a1 1 0 0 0 2 0V8a1 1 0 0 0-1-1zm0 8a1 1 0 1 0 0 2a1 1 0 0 0 0-2z"/>
          </svg>
          API Interactive Demo
        </h1>
        <p>Experience the complete Terrain Pro API workflow with real API calls</p>
      </div>

      <div className="progress-tracker">
        <div className="progress-bar">
          {steps.map((step) => (
            <div 
              key={step.stepNumber}
              className={`progress-step ${step.status}`}
              style={{ borderColor: getStatusColor(step.status) }}
            >
              <span className="progress-step-number">{step.stepNumber}</span>
            </div>
          ))}
        </div>
        <p className="progress-text">Step {currentStep} of 5</p>
      </div>

      <div className="steps-container">
        {steps.map((step) => {
          const stepInfo = getStepInfo(step.stepNumber);
          const duration = step.startTime && step.endTime 
            ? ((step.endTime.getTime() - step.startTime.getTime()) / 1000).toFixed(1) 
            : null;

          return (
            <div
              key={step.stepNumber}
              id={`step-${step.stepNumber}`}
              className={`step-card ${step.status} ${currentStep === step.stepNumber ? 'current' : ''}`}
            >
              <div className="step-header">
                <div className="step-title-row">
                  <span className="step-icon">{stepInfo.icon}</span>
                  <h3>Step {step.stepNumber}: {stepInfo.title}</h3>
                  <span className="status-badge" style={{ color: getStatusColor(step.status) }}>
                    {getStatusIcon(step.status)} {step.status.toUpperCase()}
                  </span>
                </div>
                <p className="step-description">{stepInfo.description}</p>
                <div className="step-api-path">
                  <span className="demo-api-method">
                    {step.stepNumber === 1 || step.stepNumber === 5 ? 'GET' : 'POST'}
                  </span>
                  <code className="demo-api-path-text">{stepInfo.apiPath}</code>
                </div>
              </div>

              {step.status !== 'locked' && (
                <div className="step-body">
                  {/* Step 1: Email login + JWT Token Input */}
                  {step.stepNumber === 1 && step.status === 'ready' && (
                    <div className="step-input">
                      <label htmlFor="email-login" className="file-label">Email Address:</label>
                      <div className="email-login-row">
                        <input
                          id="email-login"
                          type="email"
                          value={emailInput}
                          onChange={(e) => { setEmailInput(e.target.value); setEmailStatus({ type: 'idle', message: '' }); }}
                          className="file-input"
                          placeholder="Enter your registered email..."
                          onKeyDown={(e) => { if (e.key === 'Enter') handleEmailLogin(); }}
                        />
                        <button
                          className="execute-button"
                          onClick={handleEmailLogin}
                          disabled={emailStatus.type === 'loading' || !emailInput.trim()}
                          style={{marginLeft: '8px', whiteSpace: 'nowrap'}}
                        >
                          {emailStatus.type === 'loading' ? '⏳ Loading...' : '🔑 Get Token'}
                        </button>
                      </div>
                      {emailStatus.type !== 'idle' && (
                        <p className={`email-status ${emailStatus.type}`}>{emailStatus.message}</p>
                      )}

                      <div className="auth-divider"><span>or enter token manually</span></div>

                      <label htmlFor="jwt-token" className="file-label">JWT Bearer Token:</label>
                      <input
                        id="jwt-token"
                        type="password"
                        value={authToken}
                        onChange={(e) => setAuthToken(e.target.value)}
                        className="file-input"
                        placeholder="Enter your JWT Bearer token..."
                      />
                      {authToken && (
                        <p className="file-info">Token length: {authToken.length} characters</p>
                      )}
                    </div>
                  )}

                  {/* Step 2: File upload */}
                  {step.stepNumber === 2 && step.status === 'ready' && (
                    <div className="step-input">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        <label htmlFor="project-select" className="file-label" style={{ margin: 0 }}>Project:</label>
                        {projectsLoading ? (
                          <span style={{ fontSize: '12px', color: '#888', fontStyle: 'italic' }}>Loading projects...</span>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <label style={{ fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <input
                                type="radio"
                                name="projectMode"
                                checked={projectMode === 'existing'}
                                onChange={() => setProjectMode('existing')}
                                disabled={projectsList.length === 0}
                              />
                              Select Existing ({projectsList.length})
                            </label>
                            <label style={{ fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <input
                                type="radio"
                                name="projectMode"
                                checked={projectMode === 'new'}
                                onChange={() => { setProjectMode('new'); setSelectedProjectId(null); }}
                              />
                              Create New
                            </label>
                          </div>
                        )}
                      </div>

                      {projectMode === 'existing' ? (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div ref={projectDropdownRef} style={{ flex: 1, position: 'relative' }}>
                              <input
                                id="project-select"
                                className="file-input"
                                style={{ width: '100%', boxSizing: 'border-box' }}
                                value={projectDropdownOpen ? projectSearch : (
                                  selectedProjectId
                                    ? (projectsList.find(p => p.id === selectedProjectId)?.project_name || '')
                                    : ''
                                )}
                                placeholder={projectsLoading ? 'Loading projects...' : 'Search and select a project...'}
                                disabled={projectsLoading}
                                onChange={(e) => {
                                  setProjectSearch(e.target.value);
                                  setProjectDropdownOpen(true);
                                }}
                                onFocus={() => {
                                  setProjectDropdownOpen(true);
                                  setProjectSearch('');
                                }}
                                autoComplete="off"
                              />
                              {projectDropdownOpen && (
                                <div style={{
                                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                                  maxHeight: '220px', overflowY: 'auto',
                                  background: '#fff', border: '1px solid #ccc', borderTop: 'none',
                                  borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                }}>
                                  {filteredProjects.length === 0 ? (
                                    <div style={{ padding: '10px 12px', color: '#999', fontSize: '13px' }}>
                                      No projects match "{projectSearch}"
                                    </div>
                                  ) : (
                                    filteredProjects.map(proj => (
                                      <div
                                        key={proj.id}
                                        onClick={() => handleProjectSelect(proj)}
                                        style={{
                                          padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                                          borderBottom: '1px solid #f0f0f0',
                                          backgroundColor: selectedProjectId === proj.id ? '#e7f3ff' : '#fff',
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f8fc')}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = selectedProjectId === proj.id ? '#e7f3ff' : '#fff')}
                                      >
                                        <div style={{ fontWeight: 500 }}>{proj.project_name}</div>
                                        <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                                          ID: {proj.id}
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                            <code style={{ fontSize: '10px', color: '#666', whiteSpace: 'nowrap', background: '#f0f0f0', padding: '4px 6px', borderRadius: '3px' }}>
                              GET /tools/api/v1/terrain-pro/my-projects
                            </code>
                          </div>
                          {selectedProjectId && (() => {
                            const proj = projectsList.find(p => p.id === selectedProjectId);
                            if (!proj) return null;
                            return (
                              <div style={{ backgroundColor: '#f0f7ff', padding: '8px 10px', borderRadius: '4px', marginTop: '6px', fontSize: '12px', border: '1px solid #c8ddf0' }}>
                                <strong>{proj.project_name}</strong>
                                <span style={{ marginLeft: '10px', color: '#888' }}>ID: {proj.id}</span>
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <div>
                          <input
                            id="project-name"
                            type="text"
                            value={projectName}
                            onChange={(e) => setProjectName(e.target.value)}
                            className="file-input"
                            placeholder="Enter project name *"
                          />
                          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                            <div style={{ flex: 1 }}>
                              <label htmlFor="project-lat" className="file-label" style={{ fontSize: '12px' }}>Latitude:</label>
                              <input
                                id="project-lat"
                                type="number"
                                step="any"
                                value={projectLat}
                                onChange={(e) => {
                                  setProjectLat(e.target.value);
                                  reverseGeocode(e.target.value, projectLon);
                                }}
                                className="file-input"
                                placeholder="e.g. 44.789060"
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label htmlFor="project-lon" className="file-label" style={{ fontSize: '12px' }}>Longitude:</label>
                              <input
                                id="project-lon"
                                type="number"
                                step="any"
                                value={projectLon}
                                onChange={(e) => {
                                  setProjectLon(e.target.value);
                                  reverseGeocode(projectLat, e.target.value);
                                }}
                                className="file-input"
                                placeholder="e.g. 22.457546"
                              />
                            </div>
                          </div>
                          {geoLoading && (
                            <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#888', fontStyle: 'italic' }}>
                              Resolving location, elevation &amp; timezone...
                            </p>
                          )}
                          {!geoLoading && geoInfo && (geoInfo.city || geoInfo.state || geoInfo.country || geoInfo.elevation != null || geoInfo.timezone != null) && (
                            <div style={{ backgroundColor: '#f0f7ff', padding: '8px 10px', borderRadius: '4px', marginTop: '6px', fontSize: '12px', border: '1px solid #c8ddf0' }}>
                              {geoInfo.city && <span><strong>City:</strong> {geoInfo.city}</span>}
                              {geoInfo.state && <span style={{ marginLeft: '12px' }}><strong>State:</strong> {geoInfo.state}</span>}
                              {geoInfo.country && <span style={{ marginLeft: '12px' }}><strong>Country:</strong> {geoInfo.country}</span>}
                              {geoInfo.elevation != null && <span style={{ marginLeft: '12px' }}><strong>Elevation:</strong> {geoInfo.elevation} m</span>}
                              {geoInfo.timezone != null && <span style={{ marginLeft: '12px' }}><strong>Timezone:</strong> UTC{geoInfo.timezone >= 0 ? '+' : ''}{geoInfo.timezone}</span>}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <label htmlFor="layout-name" className="file-label" style={{marginTop: '10px'}}>Layout Name:</label>
                      <input id="layout-name" type="text" value={layoutName} onChange={(e) => setLayoutName(e.target.value)} className="file-input" placeholder="Enter layout name" />
                      
                      <div style={{ backgroundColor: '#e7f3ff', padding: '10px', borderRadius: '3px', marginTop: '10px', marginBottom: '10px', border: '1px solid #2B5390' }}>
                        <p style={{margin: 0, fontSize: '12px', color: '#2B5390'}}>
                          ℹ️ <strong>Note:</strong> For API demo purposes, please upload DXF files less than 40 MB.
                        </p>
                      </div>
                      
                      <label htmlFor="file-upload" className="file-label">Select Terrain File:</label>
                      <input id="file-upload" type="file" onChange={handleFileChange} accept=".dxf,.xlsx,.csv" className="file-input" />
                      {selectedFile && (
                        <p className="file-info">Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)</p>
                      )}
                    </div>
                  )}

                  {/* Step 3: Layer/Block/Pile-Type selection + Elevation source */}
                  {step.stepNumber === 3 && step.status === 'ready' && (availableLayers.length > 0 || availableBlocks.length > 0 || availableNestedBlocks.length > 0) && (
                    <div className="step-input">
                      {/* Elevation Source Tabs */}
                      <div className="elevation-source-section">
                        <label className="file-label">Elevation Data:</label>
                        <div className="run-mode-toggle" style={{marginBottom: '10px'}}>
                          <button className={`mode-toggle-btn ${elevationSource === 'contour' ? 'active' : ''}`} onClick={() => setElevationSource('contour')}>Contour Lines</button>
                          <button className={`mode-toggle-btn ${elevationSource === 'satellite' ? 'active' : ''}`} onClick={() => setElevationSource('satellite')}>Satellite Data</button>
                        </div>

                        {elevationSource === 'satellite' && (
                          <div className="sat-search-section">
                            <div className="sat-search-row">
                              <div className="sat-field">
                                <label className="file-label">Latitude</label>
                                <input type="number" step="any" value={satLatitude} onChange={(e) => setSatLatitude(e.target.value)} className="file-input" placeholder="e.g. 37.5738" />
                              </div>
                              <div className="sat-field">
                                <label className="file-label">Longitude</label>
                                <input type="number" step="any" value={satLongitude} onChange={(e) => setSatLongitude(e.target.value)} className="file-input" placeholder="e.g. -5.1511" />
                              </div>
                              <div className="sat-field">
                                <label className="file-label">EPSG</label>
                                <input type="text" value={satEpsg} onChange={(e) => setSatEpsg(e.target.value)} className="file-input" placeholder="e.g. 25830" />
                              </div>
                              <div className="sat-field sat-field-btn">
                                <button className="execute-button" onClick={handleSatelliteSearch} disabled={satelliteSearching || !satLatitude || !satLongitude || !satEpsg.trim()} style={{marginTop: '18px'}}>
                                  {satelliteSearching ? 'Searching...' : 'Search'}
                                </button>
                              </div>
                            </div>

                            {satelliteError && (
                              <p className="sat-error">{satelliteError}</p>
                            )}

                            {satelliteResults.length > 0 && (
                              <div className="sat-results-table-wrap">
                                <table className="sat-results-table">
                                  <thead>
                                    <tr>
                                      <th></th>
                                      <th>Name</th>
                                      <th>Pixel (m)</th>
                                      <th>Source</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {satelliteResults.map((sat, idx) => (
                                      <tr key={idx} className={selectedSatellite?.datasetId === sat.datasetId ? 'sat-row-selected' : ''} onClick={() => setSelectedSatellite(sat)}>
                                        <td><input type="radio" name="sat-select" checked={selectedSatellite?.datasetId === sat.datasetId} onChange={() => setSelectedSatellite(sat)} /></td>
                                        <td title={sat.productName}>{sat.productName?.length > 20 ? sat.productName.substring(0, 20) + '...' : sat.productName}</td>
                                        <td>{typeof sat.pixelSize === 'number' ? sat.pixelSize.toFixed(2) : sat.pixelSize}</td>
                                        <td title={sat.source}>{sat.source?.length > 25 ? sat.source.substring(0, 25) + '...' : sat.source}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {selectedSatellite && (
                              <div className="sat-selected-badge">
                                Selected: <strong>{selectedSatellite.productName}</strong> ({selectedSatellite.pixelSize}m, {selectedSatellite.source})
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Block/Layer selection grid */}
                      <div className="extraction-selection-grid">
                        {elevationSource === 'contour' && availableLayers.length > 0 && (
                          <div className="extraction-selection-column">
                            <label className="file-label">Select Elevation Layers:</label>
                            {availableLayers.map(layer => (
                              <div key={layer} style={{marginBottom: '5px'}}>
                                <label>
                                  <input type="checkbox" checked={selectedLayers.includes(layer)} onChange={() => handleLayerToggle(layer)} />
                                  {' '}{layer}
                                </label>
                              </div>
                            ))}
                          </div>
                        )}

                        {availableNestedBlocks.length > 0 && (
                          <div className="extraction-selection-column">
                            <label className="file-label">Select Pile Types (Nested Blocks):</label>
                            {availableNestedBlocks.map(nb => (
                              <div key={nb} style={{marginBottom: '5px'}}>
                                <label>
                                  <input type="checkbox" checked={selectedNestedBlocks.includes(nb)} onChange={() => handleNestedBlockToggle(nb)} />
                                  {' '}{nb}
                                </label>
                              </div>
                            ))}
                          </div>
                        )}

                        {availableBlocks.length > 0 && (
                          <div className="extraction-selection-column">
                            <label className="file-label">Select Rack Blocks:</label>
                            {availableBlocks.map(block => (
                              <div key={block} style={{marginBottom: '5px'}}>
                                <label>
                                  <input type="checkbox" checked={selectedBlocks.includes(block)} onChange={() => handleBlockToggle(block)} />
                                  {' '}{block}
                                </label>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Step 3 (CSV): manual Unit / EPSG — CSV carries no embedded
                      unit or coordinate system, so the user must supply them. */}
                  {step.stepNumber === 3 && step.status === 'ready' && steps[1]?.responseData?.file_type === 'CSV' && (
                    <div className="step-input">
                      <div style={{ backgroundColor: '#e7f3ff', padding: '10px', borderRadius: '3px', marginBottom: '10px', border: '1px solid #2B5390' }}>
                        <p style={{ margin: 0, fontSize: '12px', color: '#2B5390' }}>
                          ℹ️ <strong>Note:</strong> CSV files have no embedded unit or coordinate system. These were pre-filled from the file name where possible — please confirm or correct them before extracting.
                        </p>
                      </div>
                      <div className="sat-search-row">
                        <div className="sat-field">
                          <label className="file-label">Drawing Unit</label>
                          <select value={csvUnit} onChange={(e) => setCsvUnit(e.target.value as 'SI' | 'Imperial')} className="file-input">
                            <option value="SI">SI (meters)</option>
                            <option value="Imperial">Imperial (feet)</option>
                          </select>
                        </div>
                        <div className="sat-field">
                          <label className="file-label">EPSG</label>
                          <input type="text" value={csvEpsg} onChange={(e) => setCsvEpsg(e.target.value)} className="file-input" placeholder="e.g. 32630" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 3 (XLSX): drawing unit, EPSG, PV module / rack info,
                      and PV-area selection table. Mirrors the Angular
                      xlsx-upload modal's "Create analysis" page two. */}
                  {step.stepNumber === 3 && step.status === 'ready' && steps[1]?.responseData?.file_type === 'XLSX' && (
                    <div className="step-input">
                      <div style={{ backgroundColor: '#e7f3ff', padding: '10px', borderRadius: '3px', marginBottom: '10px', border: '1px solid #2B5390' }}>
                        <p style={{ margin: 0, fontSize: '12px', color: '#2B5390' }}>
                          ℹ️ <strong>Note:</strong> Confirm drawing unit, EPSG, and pick which PV areas to include in the extraction.
                        </p>
                      </div>

                      <div className="sat-search-row">
                        <div className="sat-field">
                          <label className="file-label">Drawing Unit</label>
                          <select value={xlsxUnit} onChange={(e) => setXlsxUnit(e.target.value as 'SI' | 'Imperial')} className="file-input">
                            <option value="SI">SI (meters)</option>
                            <option value="Imperial">Imperial (feet)</option>
                          </select>
                        </div>
                        <div className="sat-field">
                          <label className="file-label">EPSG</label>
                          <input type="text" value={xlsxEpsg} onChange={(e) => setXlsxEpsg(e.target.value)} className="file-input" placeholder="e.g. 32630" />
                        </div>
                      </div>

                      {xlsxPvModuleInfo && (
                        <div style={{ marginTop: '12px' }}>
                          <label className="file-label">PV module info</label>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '6px 16px', fontSize: '12px', padding: '8px', background: '#f7f7f7', borderRadius: '3px' }}>
                            {xlsxPvModuleInfo.power != null && <span>Power: <strong>{xlsxPvModuleInfo.power} Wp</strong></span>}
                            {xlsxPvModuleInfo.length != null && <span>Length: <strong>{xlsxPvModuleInfo.length} {xlsxUnit === 'Imperial' ? 'ft' : 'm'}</strong></span>}
                            {xlsxPvModuleInfo.width != null && <span>Width: <strong>{xlsxPvModuleInfo.width} {xlsxUnit === 'Imperial' ? 'ft' : 'm'}</strong></span>}
                            {xlsxPvModuleInfo.thickness != null && <span>Thickness: <strong>{xlsxPvModuleInfo.thickness} {xlsxUnit === 'Imperial' ? 'ft' : 'm'}</strong></span>}
                          </div>
                        </div>
                      )}

                      {xlsxRackInfo.length > 0 && (
                        <div style={{ marginTop: '12px' }}>
                          <label className="file-label">Rack info</label>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ background: '#f0f0f0' }}>
                                  <th style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd' }}>Name</th>
                                  <th style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd' }}>Piles</th>
                                  <th style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd' }}>Modules</th>
                                  <th style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd' }}>Column Gap</th>
                                  <th style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd' }}>Row Gap</th>
                                </tr>
                              </thead>
                              <tbody>
                                {xlsxRackInfo.map((rack: any, idx: number) => (
                                  <tr key={rack.name ?? idx}>
                                    <td style={{ padding: '6px', border: '1px solid #ddd' }}>{rack.name ?? '—'}</td>
                                    <td style={{ padding: '6px', border: '1px solid #ddd' }}>{rack.piles ?? '—'}</td>
                                    <td style={{ padding: '6px', border: '1px solid #ddd' }}>{rack.modules ?? '—'}</td>
                                    <td style={{ padding: '6px', border: '1px solid #ddd' }}>{rack.column_gap ?? rack.columnGap ?? '—'}</td>
                                    <td style={{ padding: '6px', border: '1px solid #ddd' }}>{rack.row_gap ?? rack.rowGap ?? '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {xlsxAreas.length > 0 && (() => {
                        // Dynamic columns: every key on an area row except identity
                        // / control fields. Matches Angular getDynamicKeys().
                        const reserved = new Set(['name', 'area_name', 'id', 'numOfRacks', 'num_of_racks', 'row', 'selected']);
                        const dynamicKeys = Object.keys(xlsxAreas[0]).filter(k => !reserved.has(k));
                        const allSelected = xlsxSelectedRows.length === xlsxAreas.length;
                        const toggleAll = () => {
                          setXlsxSelectedRows(allSelected ? [] : xlsxAreas.map(a => a.row));
                        };
                        const toggleRow = (row: number) => {
                          setXlsxSelectedRows(prev => prev.includes(row) ? prev.filter(r => r !== row) : [...prev, row]);
                        };
                        return (
                          <div style={{ marginTop: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                              <input type="checkbox" id="xlsx-select-all" checked={allSelected} onChange={toggleAll} />
                              <label htmlFor="xlsx-select-all" className="file-label" style={{ margin: 0 }}>Information by area</label>
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ background: '#f0f0f0' }}>
                                    <th style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd', width: '40px' }}>Select</th>
                                    <th style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd' }}>Area name</th>
                                    {dynamicKeys.map(k => (
                                      <th key={k} style={{ padding: '6px', textAlign: 'left', border: '1px solid #ddd' }}>{k}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {xlsxAreas.map((area: any) => {
                                    const checked = xlsxSelectedRows.includes(area.row);
                                    return (
                                      <tr key={area.row} style={{ cursor: 'pointer', background: checked ? '#e1efd6' : undefined }} onClick={() => toggleRow(area.row)}>
                                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>
                                          <input type="checkbox" checked={checked} onChange={() => toggleRow(area.row)} onClick={(e) => e.stopPropagation()} />
                                        </td>
                                        <td style={{ padding: '6px', border: '1px solid #ddd' }}>{area.name ?? area.area_name ?? '—'}</td>
                                        {dynamicKeys.map(k => (
                                          <td key={k} style={{ padding: '6px', border: '1px solid #ddd' }}>{area[k] ?? '—'}</td>
                                        ))}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Step 4: Run mode toggle and batch inputs */}
                  {step.stepNumber === 4 && step.status === 'ready' && (
                    <div className="step-input">

                      {/* Existing Configuration Banner */}
                      {existingConfig && (
                        <div style={{ backgroundColor: '#e1efd6', padding: '12px', borderRadius: '5px', marginBottom: '15px', border: '1px solid #4e8e18' }}>
                          <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600, color: '#4e8e18' }}>
                            ✓ Default configuration created during extraction — {existingConfig.configuration_name || 'Configuration 1'} (ID: {existingConfig.id})
                          </p>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '4px 16px', fontSize: '12px', color: '#333' }}>
                            <span>Min Pile Reveal: <strong>{existingConfig.min_pile_reveal ?? '—'} {lengthUnit}</strong></span>
                            <span>Max Slope N: <strong>{existingConfig.max_tt_axis_slope_north ?? existingConfig.max_tt_axis_slope ?? '—'}°</strong></span>
                            <span>Max Slope S: <strong>{existingConfig.max_tt_axis_slope_south ?? existingConfig.max_tt_axis_slope ?? '—'}°</strong></span>
                            <span>Reveal Window: <strong>{Math.round((existingConfig.reveal_window ?? 0) * (sourceUnit === 'Imperial' ? 12 : 1000))} {revealUnit}</strong></span>
                            <span>Max Slope Change: <strong>{existingConfig.max_slope_change ?? 0}°</strong></span>
                            <span>Algorithm: <strong>v{existingConfig.algorithm_version ?? '—'}</strong></span>
                            <span>Status: <strong>{existingConfig.status ?? '—'}</strong></span>
                          </div>
                        </div>
                      )}
                      {existingConfigLoading && (
                        <p style={{ fontSize: '12px', color: '#856404', marginBottom: '10px' }}>Loading existing configuration...</p>
                      )}

                      {/* 3-way run mode toggle */}
                      <div className="run-mode-toggle">
                        <button className={`mode-toggle-btn ${runMode === 'default' ? 'active' : ''}`} onClick={() => setRunMode('default')} disabled={!existingConfig}>Run Default</button>
                        <button className={`mode-toggle-btn ${runMode === 'single' ? 'active' : ''}`} onClick={() => setRunMode('single')}>New Single Run</button>
                        <button className={`mode-toggle-btn ${runMode === 'batch' ? 'active' : ''}`} onClick={() => setRunMode('batch')}>Batch Run</button>
                      </div>

                      {/* Tracker-vendor preset selector (prefills the editable params for single/batch runs) */}
                      {runMode !== 'default' && (
                        <div style={{marginBottom: '12px'}}>
                          <label className="file-label">Tracker Vendor Preset:</label>
                          <select
                            className="file-input"
                            value={selectedPreset}
                            onChange={(e) => setSelectedPreset(e.target.value)}
                          >
                            <option value="Custom">Custom (manual values)</option>
                            {Object.keys(VENDOR_PRESETS).map(name => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                          </select>
                          <span className="batch-input-hint">
                            {selectedPreset === 'Custom'
                              ? 'Enter parameters manually below.'
                              : `Prefilled from ${selectedPreset} for ${sourceUnit === 'Imperial' ? 'Imperial (ft / in)' : 'SI (m / mm)'} — editable below, then sent to the run API.`}
                          </span>
                        </div>
                      )}

                      {/* Default config mode */}
                      {runMode === 'default' && existingConfig && (
                        <div style={{ backgroundColor: '#f0f8ff', padding: '10px', borderRadius: '5px', marginBottom: '10px', border: '1px solid #2B5390' }}>
                          <p style={{margin: '0 0 6px 0', fontSize: '13px', color: '#2B5390'}}>
                            Run the default <strong>{existingConfig.configuration_name || 'Configuration 1'}</strong> created during extraction.
                            The appropriate solution chain will be determined automatically based on its parameters.
                          </p>
                        </div>
                      )}

                      {runMode === 'batch' && (
                        <div className="batch-params-section">
                          <div style={{ backgroundColor: '#f0f8ff', padding: '10px', borderRadius: '5px', marginBottom: '15px', border: '1px solid #2B5390' }}>
                            <p style={{margin: 0, fontSize: '13px', color: '#2B5390'}}>
                              Batch mode creates a Cartesian product of reveal windows and articulation angles, generating multiple configurations and running them all in parallel.
                            </p>
                          </div>

                          <div className="batch-base-params">
                            <label className="file-label">Base Parameters:</label>
                            <div className="batch-param-row">
                              <div className="batch-param-field">
                                <label className="file-label">Min Pile Reveal ({lengthUnit}):</label>
                                <input type="number" value={configParams.min_pile_reveal} onChange={(e) => setConfigParams({...configParams, min_pile_reveal: parseFloat(e.target.value)})} className="file-input" step="0.1" />
                              </div>
                              <div className="batch-param-field">
                                <label className="file-label">Max Slope North (deg):</label>
                                <input type="number" value={configParams.max_slope_north} onChange={(e) => setConfigParams({...configParams, max_slope_north: parseFloat(e.target.value)})} className="file-input" step="0.1" />
                              </div>
                              <div className="batch-param-field">
                                <label className="file-label">Max Slope South (deg):</label>
                                <input type="number" value={configParams.max_slope_south} onChange={(e) => setConfigParams({...configParams, max_slope_south: parseFloat(e.target.value)})} className="file-input" step="0.1" />
                              </div>
                            </div>
                          </div>

                          <div style={{marginTop: '15px'}}>
                            <label className="file-label">Reveal Windows in {revealUnit} (comma-separated):</label>
                            <input type="text" value={batchParams.reveal_windows} onChange={(e) => setBatchParams({...batchParams, reveal_windows: e.target.value})} className="file-input" placeholder={sourceUnit === 'Imperial' ? 'e.g. 4, 8, 12' : 'e.g. 100, 200, 300'} />
                            <span className="batch-input-hint">
                              {(() => { const c = batchParams.reveal_windows.split(',').map(v => v.trim()).filter(v => v && !isNaN(Number(v))).length; return `${c} value${c !== 1 ? 's' : ''}`; })()}
                            </span>
                          </div>

                          <div style={{marginTop: '10px'}}>
                            <label className="file-label">Max Articulation Angles in deg (comma-separated):</label>
                            <input type="text" value={batchParams.max_articulation_angles} onChange={(e) => setBatchParams({...batchParams, max_articulation_angles: e.target.value})} className="file-input" placeholder="e.g. 0, 0.75, 1.5" />
                            <span className="batch-input-hint">
                              {(() => { const c = batchParams.max_articulation_angles.split(',').map(v => v.trim()).filter(v => v && !isNaN(Number(v))).length; return `${c} value${c !== 1 ? 's' : ''}`; })()}
                            </span>
                          </div>

                          <div style={{marginTop: '10px'}}>
                            <label className="file-label">Grading Objective:</label>
                            <select
                              className="file-input"
                              value={configParams.optimization_goal}
                              onChange={(e) => setConfigParams({...configParams, optimization_goal: e.target.value as 'earthwork' | 'pileReveal'})}
                            >
                              <option value="earthwork">Minimize earthwork</option>
                              <option value="pileReveal">Minimize pile reveal</option>
                            </select>
                            <span className="batch-input-hint">
                              Applied to combinations that run Solution Four (reveal window &gt; 0 and angle &gt; 0).
                            </span>
                          </div>

                          {(() => {
                            const rw = batchParams.reveal_windows.split(',').map(v => v.trim()).filter(v => v && !isNaN(Number(v)));
                            const aa = batchParams.max_articulation_angles.split(',').map(v => v.trim()).filter(v => v && !isNaN(Number(v)));
                            const combos = rw.length * aa.length;
                            return combos > 0 ? (
                              <div className="batch-preview">
                                <strong>Preview:</strong> {rw.length} reveal window{rw.length !== 1 ? 's' : ''} &times; {aa.length} angle{aa.length !== 1 ? 's' : ''} = <strong>{combos} combinations</strong> (+ Sol1 baseline)
                              </div>
                            ) : null;
                          })()}
                        </div>
                      )}

                      {runMode === 'single' && (
                        <div style={{ backgroundColor: '#f0f8ff', padding: '10px', borderRadius: '5px', marginBottom: '10px', border: '1px solid #2B5390' }}>
                          <p style={{margin: '0 0 6px 0', fontSize: '13px', color: '#2B5390'}}>
                            Creates a <strong>new</strong> configuration and runs the solution chain based on the parameters below.
                            Sol1 always runs first; Sol3 and Sol4 start automatically once Sol1 completes.
                          </p>
                          <p style={{margin: '0 0 10px 0', fontSize: '12px', fontWeight: 600,
                            color: configParams.reveal_window > 0 && configParams.max_articulation_angle > 0 ? '#4e8e18' : '#2B5390'}}>
                            {configParams.reveal_window === 0 && configParams.max_articulation_angle === 0 && '▶ Sol1 only (RW=0, Angle=0)'}
                            {configParams.reveal_window > 0 && configParams.max_articulation_angle === 0 && '▶ Sol1 → Sol2 (RW>0, Angle=0)'}
                            {configParams.reveal_window === 0 && configParams.max_articulation_angle > 0 && '▶ Sol1 → Sol3 (RW=0, Angle>0)'}
                            {configParams.reveal_window > 0 && configParams.max_articulation_angle > 0 && '▶ Sol1 → Sol3 → Sol4 (RW>0, Angle>0)'}
                          </p>
                          <div className="batch-param-row">
                            <div className="batch-param-field">
                              <label className="file-label">Min Pile Reveal ({lengthUnit})</label>
                              <input type="number" value={configParams.min_pile_reveal} onChange={(e) => setConfigParams({...configParams, min_pile_reveal: parseFloat(e.target.value)})} className="file-input" step="0.1" />
                            </div>
                            <div className="batch-param-field">
                              <label className="file-label">Max Slope North (deg)</label>
                              <input type="number" value={configParams.max_slope_north} onChange={(e) => setConfigParams({...configParams, max_slope_north: parseFloat(e.target.value)})} className="file-input" step="0.1" />
                            </div>
                            <div className="batch-param-field">
                              <label className="file-label">Max Slope South (deg)</label>
                              <input type="number" value={configParams.max_slope_south} onChange={(e) => setConfigParams({...configParams, max_slope_south: parseFloat(e.target.value)})} className="file-input" step="0.1" />
                            </div>
                          </div>
                          <div className="batch-param-row" style={{marginTop: '8px'}}>
                            <div className="batch-param-field">
                              <label className="file-label">Reveal Window ({revealUnit})</label>
                              <input type="number" value={configParams.reveal_window} onChange={(e) => setConfigParams({...configParams, reveal_window: parseFloat(e.target.value)})} className="file-input" step="1" min="0" />
                            </div>
                            <div className="batch-param-field">
                              <label className="file-label">Max Articulation Angle (deg)</label>
                              <input type="number" value={configParams.max_articulation_angle} onChange={(e) => setConfigParams({...configParams, max_articulation_angle: parseFloat(e.target.value)})} className="file-input" step="0.25" min="0" />
                            </div>
                            <div className="batch-param-field">
                              <label className="file-label">Algorithm Version</label>
                              <input type="number" value={configParams.algorithm_version} onChange={(e) => setConfigParams({...configParams, algorithm_version: parseFloat(e.target.value)})} className="file-input" step="0.1" />
                            </div>
                          </div>
                          <div className="batch-param-row" style={{marginTop: '8px'}}>
                            <div className="batch-param-field" style={{flex: 1}}>
                              <label className="file-label">Grading Objective</label>
                              <select
                                className="file-input"
                                value={configParams.optimization_goal}
                                onChange={(e) => setConfigParams({...configParams, optimization_goal: e.target.value as 'earthwork' | 'pileReveal'})}
                              >
                                <option value="earthwork">Minimize earthwork</option>
                                <option value="pileReveal">Minimize pile reveal</option>
                              </select>
                              <span className="batch-input-hint">
                                {configParams.reveal_window > 0 && configParams.max_articulation_angle > 0
                                  ? 'Forwarded to Solution Four to drive the grading optimization.'
                                  : 'Only applied when reveal window > 0 and articulation angle > 0 (Solution Four).'}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {step.status === 'running' && (
                    <div className="step-loading">
                      <div className="loading-spinner"></div>
                      <div style={{flex: 1}}>
                        <p>Processing...</p>
                        {step.statusMessage && <p style={{fontSize: '12px', marginTop: '5px'}}>{step.statusMessage}</p>}

                        {/* Step 2: Upload progress bar */}
                        {step.stepNumber === 2 && uploadProgress > 0 && (
                          <div className="upload-progress">
                            <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }} />
                          </div>
                        )}

                        {/* Step 3: Extraction progress bar */}
                        {step.stepNumber === 3 && (
                          <div className="upload-progress">
                            <div
                              className={`upload-progress-bar ${extractionProgress === 0 ? 'indeterminate' : ''}`}
                              style={extractionProgress > 0 ? { width: `${extractionProgress}%` } : undefined}
                            />
                          </div>
                        )}
                        
                        {/* Generic: Request payload for any step while running */}
                        {Object.keys(step.requestData).length > 0 && (
                          <details style={{marginTop: '8px', fontSize: '12px'}}>
                            <summary style={{cursor: 'pointer', fontWeight: 'bold', color: '#856404'}}>View Request Payload</summary>
                            <pre style={{background: '#1b3359', color: '#fff', padding: '8px', borderRadius: '3px', marginTop: '6px', fontSize: '11px', overflow: 'auto', maxHeight: '250px'}}>
                              {JSON.stringify(step.requestData, null, 2)}
                            </pre>
                          </details>
                        )}

                        {/* Step 4: Single mode Sol1 status + job statuses while running */}
                        {step.stepNumber === 4 && step.responseData && step.responseData.jobs && !step.responseData.configurations && (
                          <div style={{marginTop: '10px', fontSize: '12px'}}>
                            {step.responseData.sol1_status && (
                              <div className="sol1-status-banner" style={{
                                padding: '6px 10px', marginBottom: '8px', borderRadius: '3px',
                                background: step.responseData.sol1_status === 'exists' ? '#e1efd6' : step.responseData.sol1_status === 'created' ? '#fff3cd' : '#d9f0fc',
                                border: `1px solid ${step.responseData.sol1_status === 'exists' ? '#4e8e18' : step.responseData.sol1_status === 'created' ? '#f39c12' : '#2B5390'}`,
                                color: step.responseData.sol1_status === 'exists' ? '#4e8e18' : step.responseData.sol1_status === 'created' ? '#856404' : '#2B5390'
                              }}>
                                <strong>Solution 1:</strong>{' '}
                                {step.responseData.sol1_status === 'exists' && '✓ Already completed (reusing existing Sol1)'}
                                {step.responseData.sol1_status === 'created' && '⏳ Created and queued (running in background)'}
                                {step.responseData.sol1_status === 'running' && '⏳ Already running (waiting for completion)'}
                              </div>
                            )}
                            <p style={{fontWeight: 'bold', marginBottom: '5px'}}>Jobs:</p>
                            {step.responseData.jobs.map((job: any, index: number) => (
                              <div key={index} style={{marginTop: '3px', paddingLeft: '10px'}}>
                                <span style={{color: getJobColor(job.status)}}>{getJobIcon(job.status)}</span>
                                {' '}{(job.job_type || '').replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                {' - '}<span style={{color: getJobColor(job.status)}}>{job.status}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Step 4: Batch config/job statuses while running */}
                        {step.stepNumber === 4 && step.responseData && step.responseData.configurations && (
                          <div style={{marginTop: '10px', fontSize: '12px'}}>
                            {step.responseData.configurations.map((config: any, cIdx: number) => {
                              const params = config.parameters || {};
                              return (
                                <div key={cIdx} className="batch-config-group">
                                  <div className="batch-config-header">
                                    Config {cIdx + 1} (reveal: {params.reveal ?? params.reveal_window ?? '—'}, angle: {params.angle ?? params.max_articulation_angle ?? '—'})
                                  </div>
                                  {(config.jobs || []).map((job: any, jIdx: number) => (
                                    <div key={jIdx} style={{marginTop: '2px', paddingLeft: '15px'}}>
                                      <span style={{color: getJobColor(job.status)}}>{getJobIcon(job.status)}</span>
                                      {' '}{(job.job_type || job.type || '').replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                      {' - '}<span style={{color: getJobColor(job.status)}}>{job.status}</span>
                                    </div>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {step.status === 'success' && step.responseData && (
                    <div className="step-response">
                      <h4>✓ Success</h4>
                      {duration && <p className="duration">Completed in {duration}s</p>}

                      {/* Generic: Request payload in success view */}
                      {Object.keys(step.requestData).length > 0 && (
                        <details style={{marginBottom: '10px'}}>
                          <summary style={{cursor: 'pointer', fontWeight: 500, fontSize: '13px', color: '#2B5390'}}>View Request Payload</summary>
                          <pre style={{background: '#1b3359', color: '#fff', padding: '8px', borderRadius: '3px', marginTop: '6px', fontSize: '11px', overflow: 'auto', maxHeight: '250px'}}>
                            {JSON.stringify(step.requestData, null, 2)}
                          </pre>
                        </details>
                      )}
                      
                      {/* Step 4: Single mode Sol1 status + job statuses */}
                      {step.stepNumber === 4 && step.responseData.jobs && !step.responseData.configurations && (
                        <div style={{marginTop: '15px'}}>
                          {step.responseData.sol1_status && (
                            <div style={{
                              padding: '6px 10px', marginBottom: '10px', borderRadius: '3px',
                              background: step.responseData.sol1_status === 'exists' ? '#e1efd6' : '#fff3cd',
                              border: `1px solid ${step.responseData.sol1_status === 'exists' ? '#4e8e18' : '#f39c12'}`,
                              fontSize: '0.9em'
                            }}>
                              <strong>Solution 1:</strong>{' '}
                              {step.responseData.sol1_status === 'exists' && '✓ Already completed (reused existing)'}
                              {step.responseData.sol1_status === 'created' && '✓ Created and completed'}
                              {step.responseData.sol1_status === 'running' && '✓ Completed'}
                            </div>
                          )}
                          <h5>Analysis Jobs:</h5>
                          {step.responseData.jobs.map((job: any, index: number) => (
                            <div key={index} style={{marginTop: '8px', fontSize: '0.9em'}}>
                              <span style={{color: getJobColor(job.status), fontSize: '1.2em', marginRight: '5px'}}>{getJobIcon(job.status)}</span>
                              <span style={{fontWeight: 'bold'}}>{(job.job_type || '').replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}</span>
                              <span style={{marginLeft: '10px', color: getJobColor(job.status)}}>{job.status}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Step 4: Batch configurations and jobs */}
                      {step.stepNumber === 4 && step.responseData.configurations && (
                        <div style={{marginTop: '15px'}}>
                          <h5>Batch Analysis Results:</h5>
                          <div className="batch-summary">
                            {step.responseData.total_configs} configuration{step.responseData.total_configs !== 1 ? 's' : ''}, {step.responseData.total_jobs} total job{step.responseData.total_jobs !== 1 ? 's' : ''}
                            {step.responseData.sol1_status && <span> &mdash; Sol1: {step.responseData.sol1_status}</span>}
                          </div>
                          {step.responseData.configurations.map((config: any, cIdx: number) => {
                            const params = config.parameters || {};
                            return (
                              <div key={cIdx} className="batch-config-group" style={{marginTop: '10px'}}>
                                <div className="batch-config-header">
                                  Config {cIdx + 1} &mdash; reveal: {params.reveal ?? params.reveal_window ?? '—'}, angle: {params.angle ?? params.max_articulation_angle ?? '—'}
                                </div>
                                {(config.jobs || []).map((job: any, jIdx: number) => (
                                  <div key={jIdx} style={{marginTop: '4px', paddingLeft: '15px', fontSize: '0.9em'}}>
                                    <span style={{color: getJobColor(job.status), fontSize: '1.2em', marginRight: '5px'}}>{getJobIcon(job.status)}</span>
                                    <span style={{fontWeight: 'bold'}}>{(job.job_type || job.type || '').replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}</span>
                                    <span style={{marginLeft: '10px', color: getJobColor(job.status)}}>{job.status}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Step 5: Single download links */}
                      {step.stepNumber === 5 && step.responseData.outputs && !step.responseData.batch_outputs && (
                        <div style={{marginTop: '15px'}}>
                          <h5>Available Downloads:</h5>
                          {step.responseData.outputs.map((output: any, index: number) => (
                            <div key={index} style={{marginTop: '8px'}}>
                              <a href={output.download_url} target="_blank" rel="noopener noreferrer" style={{color: '#2B5390', textDecoration: 'underline'}}>
                                📄 {output.display_name || output.output_type}
                              </a>
                              {output.expires_in && (
                                <span style={{marginLeft: '10px', fontSize: '0.85em', color: '#666'}}>(expires in {Math.floor(output.expires_in / 60)} min)</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Step 5: Batch download links */}
                      {step.stepNumber === 5 && step.responseData.batch_outputs && (
                        <div style={{marginTop: '15px'}}>
                          <h5>Batch Downloads ({step.responseData.total_outputs} files across {step.responseData.batch_outputs.length} configurations):</h5>
                          {step.responseData.batch_outputs.map((configOutput: any, cIdx: number) => {
                            const params = configOutput.parameters || {};
                            return (
                              <div key={cIdx} className="batch-config-group" style={{marginTop: '10px'}}>
                                <div className="batch-config-header">
                                  Config {configOutput.config_id} &mdash; reveal: {params.reveal ?? params.reveal_window ?? '—'}, angle: {params.angle ?? params.max_articulation_angle ?? '—'}
                                </div>
                                {configOutput.outputs.map((output: any, oIdx: number) => (
                                  <div key={oIdx} style={{marginTop: '6px', paddingLeft: '15px'}}>
                                    <a href={output.download_url} target="_blank" rel="noopener noreferrer" style={{color: '#2B5390', textDecoration: 'underline'}}>
                                      📄 {output.display_name || output.output_type}
                                    </a>
                                    {output.expires_in && (
                                      <span style={{marginLeft: '10px', fontSize: '0.85em', color: '#666'}}>(expires in {Math.floor(output.expires_in / 60)} min)</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      <details className="response-details">
                        <summary>View Response Data</summary>
                        <pre>{JSON.stringify(step.responseData, null, 2)}</pre>
                      </details>
                    </div>
                  )}

                  {step.status === 'error' && (
                    <div className="step-error">
                      <h4>❌ Error</h4>
                      <p>{step.errorMessage}</p>
                      <button className="retry-button" onClick={() => handleExecuteStep(step.stepNumber)}>↻ Retry</button>
                    </div>
                  )}

                  {(step.status === 'ready' || step.status === 'error') && (
                    <button
                      className="execute-button"
                      onClick={() => handleExecuteStep(step.stepNumber)}
                      disabled={
                        (step.stepNumber === 1 && !authToken.trim() && step.status === 'ready') ||
                        (step.stepNumber === 2 && step.status === 'ready' && (
                          !selectedFile ||
                          (projectMode === 'existing' && !selectedProjectId) ||
                          (projectMode === 'new' && !projectName.trim())
                        ))
                      }
                    >
                      {step.stepNumber === 4 && runMode === 'batch'
                        ? '▶️ Execute Batch Run'
                        : step.stepNumber === 4 && runMode === 'default'
                        ? `▶️ Run Default ${existingConfig?.configuration_name || 'Configuration'}`
                        : step.stepNumber === 4 && runMode === 'single'
                        ? '▶️ Create & Run New Configuration'
                        : `▶️ Execute Step ${step.stepNumber}`}
                    </button>
                  )}
                </div>
              )}

              {step.status === 'locked' && (
                <div className="step-locked">
                  <p>🔒 Complete previous steps to unlock</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Step 6 (optional): Create Map Layers — standalone card after the 5-step flow */}
      {steps[4].status === 'success' && steps[4].responseData?.configuration_id && (
        <div id="step-6" className="step-card success" style={{marginTop: '20px'}}>
          <div className="step-header">
            <div className="step-title-row">
              <span className="step-icon">🗺️</span>
              <h3>Step 6: Create Map Layers <span style={{fontSize: '0.7em', fontWeight: 400, color: '#888'}}>(Optional)</span></h3>
              <span className="status-badge" style={{ color: getStatusColor(layerState.status === 'success' ? 'success' : layerState.status === 'running' ? 'running' : layerState.status === 'error' ? 'error' : 'ready') }}>
                {layerState.status === 'success' ? '✓ SUCCESS' : layerState.status === 'running' ? '⏳ RUNNING' : layerState.status === 'error' ? '❌ ERROR' : 'READY'}
              </span>
            </div>
            <p className="step-description">
              Generate the deck.gl map layers (surface textures, elevation, terrarium, and manifest) for this configuration. Each layer becomes downloadable via a presigned S3 URL as it completes.
            </p>
            <div className="step-api-path">
              <span className="demo-api-method">POST</span>
              <code className="demo-api-path-text">/tools/api/v1/terrain-pro/configurations/{steps[4].responseData.configuration_id}/layers</code>
            </div>
          </div>

          <div className="step-body">
            {layerState.status === 'idle' && (
              <button className="execute-button" onClick={() => handleCreateLayers(steps[4].responseData!.configuration_id)}>
                🗺️ Create Map Layers
              </button>
            )}

            {(layerState.status === 'running' || layerState.status === 'success') && (
              <div>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px'}}>
                  <span>{layerState.status === 'success' ? '✓ All layers ready' : '⏳ Generating layers...'}</span>
                  <span>{layerState.completed}/{layerState.total} ({layerState.progress}%)</span>
                </div>
                <div style={{background: '#e9eef5', borderRadius: '4px', height: '8px', overflow: 'hidden'}}>
                  <div style={{width: `${layerState.progress}%`, height: '100%', background: layerState.status === 'success' ? '#4e8e18' : '#2B5390', transition: 'width 0.3s'}} />
                </div>
                <div style={{marginTop: '10px'}}>
                  {layerState.layers.map((layer: any, idx: number) => (
                    <div key={idx} style={{display: 'flex', alignItems: 'center', marginTop: '5px', fontSize: '12px'}}>
                      <span style={{marginRight: '6px'}}>{layer.status === 'completed' ? '✅' : '⏳'}</span>
                      {layer.status === 'completed' && layer.download_url ? (
                        <a href={layer.download_url} target="_blank" rel="noopener noreferrer" style={{color: '#2B5390', textDecoration: 'underline'}}>
                          {layer.name}
                        </a>
                      ) : (
                        <span style={{color: '#888'}}>{layer.name}</span>
                      )}
                      <span style={{marginLeft: '8px', color: '#aaa', fontSize: '11px'}}>{layer.layer_type}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {layerState.status === 'error' && (
              <div className="step-error" style={{marginTop: '8px'}}>
                <p style={{margin: '0 0 8px'}}>❌ {layerState.error}</p>
                <button className="retry-button" onClick={() => handleCreateLayers(steps[4].responseData!.configuration_id)}>↻ Retry</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Deep-link into the Terrain Pro web app once validation has produced a layout.
          project_id and layout_id come from the validation response (steps[1]). */}
      {steps[1].responseData?.project_id && steps[1].responseData?.layout_id && (
        <div style={{ margin: '20px 0', padding: '20px', background: '#eaf3fb', border: '1px solid #bcd6ec', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '15px', color: '#1b3359' }}>Open in Terrain Pro App</h3>
            <p style={{ margin: 0, fontSize: '13px', color: '#555' }}>
              View this layout, its configurations, and results in the Terrain Pro UI.
            </p>
          </div>
          <a
            href={`${TERRAIN_PRO_APP_BASE_URL}/tools/terrainpro/project/${steps[1].responseData.project_id}/layout/${steps[1].responseData.layout_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="execute-button"
            style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
          >
            🌐 Open Layout
          </a>
        </div>
      )}

      {steps[4].status === 'success' && (
        <div className="new-file-section">
          <div className="new-file-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 9V4l5 5h-4a1 1 0 0 1-1-1zm-2 5h2v2a1 1 0 0 0 2 0v-2h2a1 1 0 0 0 0-2h-2v-2a1 1 0 0 0-2 0v2H11a1 1 0 0 0 0 2z"/>
            </svg>
          </div>
          <h3>Workflow Complete</h3>
          <p>Ready to process another terrain file? Your authentication will be preserved.</p>
          <button className="new-file-button" onClick={handleStartNewFile}>
            Upload New File
          </button>
        </div>
      )}

      {steps[0].status === 'success' && steps[4].status !== 'success' && currentStep > 1 && (
        <div className="new-file-inline">
          <button className="new-file-link" onClick={handleStartNewFile}>
            Start over with a new file
          </button>
        </div>
      )}

      {steps[0].status === 'success' && (
        <div style={{ margin: '30px 0', padding: '24px', background: '#f8fafc', border: '1px solid #d0dce8', borderRadius: '8px' }}>
          <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', color: '#1b3359' }}>Resource Endpoints</h3>
          <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#555' }}>
            Browse the available REST resource endpoints: projects, layouts, configurations, and jobs list/detail views, plus run detail.
          </p>
          <button
            className="execute-button"
            onClick={handleExploreResources}
            disabled={resourceExplorer.loading}
          >
            {resourceExplorer.loading ? '⏳ Fetching...' : '▶️ Explore Resource Endpoints'}
          </button>

          {resourceExplorer.error && (
            <p style={{ marginTop: '12px', color: '#993300', fontSize: '13px' }}>{resourceExplorer.error}</p>
          )}

          {resourceExplorer.data && (() => {
            const d = resourceExplorer.data;

            const renderEntry = (label: string, endpoint: string, entryData: any) => (
              <details className="resource-entry">
                <summary style={{ fontSize: '13px', fontWeight: 600, color: '#1b3359', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span className="resource-chevron">▶</span>
                  <span style={{ background: '#2B5390', color: '#fff', fontSize: '10px', padding: '2px 6px', borderRadius: '3px', fontWeight: 700 }}>GET</span>
                  {label}
                  <code style={{ fontSize: '11px', color: '#555', fontWeight: 400, marginLeft: 'auto' }}>{endpoint}</code>
                </summary>
                <pre style={{ marginTop: '10px', background: '#1b3359', color: '#e8f0fb', padding: '10px', borderRadius: '4px', fontSize: '11px', overflow: 'auto', maxHeight: '280px' }}>
                  {JSON.stringify(entryData, null, 2)}
                </pre>
              </details>
            );

            const renderDetailRow = (
              apiName: string,
              description: string,
              endpointTemplate: string,
              idLabel: string,
              placeholder: string,
              value: string,
              onChange: (v: string) => void,
              onFetch: () => void,
              responseData?: any,
            ) => {
              const responses: any[] = Array.isArray(responseData)
                ? (responseData as any[]).filter(Boolean)
                : responseData ? [responseData] : [];
              return (
                <details className="resource-entry">
                  <summary style={{ fontSize: '13px', fontWeight: 600, color: '#1b3359', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span className="resource-chevron">▶</span>
                    <span style={{ background: '#2B5390', color: '#fff', fontSize: '10px', padding: '2px 6px', borderRadius: '3px', fontWeight: 700 }}>GET</span>
                    {apiName}
                    <code style={{ fontSize: '11px', color: '#555', fontWeight: 400, marginLeft: 'auto' }}>{endpointTemplate}</code>
                  </summary>
                  <div style={{ marginTop: '10px' }}>
                    <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#666' }}>{description}</p>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#555', marginBottom: '3px' }}>{idLabel}</label>
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => onChange(e.target.value)}
                          className="file-input"
                          placeholder={placeholder}
                          style={{ margin: 0 }}
                          onKeyDown={(e) => { if (e.key === 'Enter') onFetch(); }}
                        />
                      </div>
                      <button className="execute-button" onClick={onFetch} disabled={!value.trim()} style={{ whiteSpace: 'nowrap' }}>
                        ▶️ Get Detail
                      </button>
                    </div>
                    {responses.length > 0 && (
                      <div style={{ marginTop: '12px', display: 'grid', gap: '8px' }}>
                        {responses.map((item: any, i: number) => (
                          <div key={i}>
                            {item._endpoint && (
                              <code style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '4px' }}>{item._endpoint}</code>
                            )}
                            <pre style={{ margin: 0, background: '#1b3359', color: '#e8f0fb', padding: '10px', borderRadius: '4px', fontSize: '11px', overflow: 'auto', maxHeight: '280px' }}>
                              {JSON.stringify(item, null, 2)}
                            </pre>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </details>
              );
            };

            return (
              <div style={{ marginTop: '16px', display: 'grid', gap: '10px' }}>
                {renderEntry('List Projects', 'GET /tools/api/v1/terrain-pro/projects/', d.projects)}
                {renderDetailRow('Get Project Detail', 'Fetch full details for a specific project by its ID.', 'GET /projects/{id}/', 'Project ID', 'e.g. 42', resourceProjectId, setResourceProjectId, () => handleFetchDetail('project', resourceProjectId), d.projectDetail)}

                {renderEntry('List Layouts', 'GET /tools/api/v1/terrain-pro/layouts/', d.layouts)}
                {renderDetailRow('Get Layout Detail + Runs', 'Fetch layout details and list all runs associated with it.', 'GET /layouts/{id}/ + /layouts/{id}/runs', 'Layout ID', 'e.g. 7', resourceLayoutId, setResourceLayoutId, () => handleFetchDetail('layout', resourceLayoutId), [d.layoutDetail, d.layoutRuns])}

                {renderEntry('List Configurations', 'GET /tools/api/v1/terrain-pro/configurations/', d.configurations)}
                {renderDetailRow('List Outputs', 'Retrieve all completed output files (presigned S3 URLs) for a specific configuration.', 'GET /configurations/{config_id}/outputs', 'Configuration ID', 'e.g. 42', resourceConfigOutputsId, setResourceConfigOutputsId, () => handleFetchDetail('configOutputs', resourceConfigOutputsId), d.configOutputs)}
                {renderDetailRow('Get Map Layers', 'Fetch the status and presigned download URLs for all generated map layers (textures, elevation, terrarium, manifest) for a configuration.', 'GET /configurations/{config_id}/layers', 'Configuration ID', 'e.g. 42', resourceConfigLayersId, setResourceConfigLayersId, () => handleFetchDetail('configLayers', resourceConfigLayersId), d.configLayers)}

                {renderEntry('List Jobs', 'GET /tools/api/v1/terrain-pro/jobs/', d.jobs)}
                {renderDetailRow('Get Job Detail', 'Fetch the status and metadata for a specific job by its ID.', 'GET /jobs/{id}/', 'Job ID', 'e.g. 101', resourceJobId, setResourceJobId, () => handleFetchDetail('job', resourceJobId), d.jobDetail)}

                {renderDetailRow('Get Run Detail', 'Fetch the full result and metadata for a specific analysis run.', 'GET /runs/{job_id}', 'Run Job ID', 'e.g. abc-123', resourceRunJobId, setResourceRunJobId, () => handleFetchDetail('run', resourceRunJobId), d.runDetail)}
              </div>
            );
          })()}
        </div>
      )}

      <div className="demo-footer">
        <p>
          <strong>Live Demo:</strong> This demo makes real API calls to the Terrain Pro backend.
          Long-running jobs will poll for status every 3 seconds after an initial 5-second delay.
        </p>
      </div>
    </div>
  );
};

export default InteractiveDemo;
